<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import {
    CREATE_TASK_CHAT_PURPOSE,
    chatFileKind,
    chatFileKindLabel,
    chatFileLimitsSentence,
    formatChatFileSize,
    isChatFilePreviewable,
    SETUP_CHAT_PURPOSE,
    type SetupQuestion,
    setupAnswerFromChip,
    setupAnswerFromTyped,
    setupQuestion,
    type WorkProfile,
} from '@zerohuman/shared'
import {
    Button,
    type ChatEntry,
    type ChatListItem,
    type DecisionView,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    Skeleton,
    type SurfaceMember,
    ZhChatList,
    ZhDecisionCard,
    ZhEmptyState,
    ZhFileChip,
    ZhFilePreview,
    ZhPageTitle,
    ZhQuickReplies,
    ZhSectionHeading,
    ZhStatusDot,
    ZhStepList,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
    api,
    blockedReason,
    type CeoQuestionAnswer,
    type ChatAttachedFile,
    type ChatDetail,
    type ChatMessageRow,
    type ChatSessionRow,
    type ChatToolCallDecision,
    type ChatToolGrantRow,
    decisionRefusal,
    type ExecutionResource,
    failReason,
    formatAgo,
    formatDuration,
    type GateDecision,
    type GateRow,
    gateTaskLabel,
    type Member,
    type OnboardingStateView,
    type ProposedWorkBatch,
    type RunDetail,
    requestFailure,
    runStoppedLine,
} from '@/api'
import AssemblyLog from '@/components/AssemblyLog.vue'
import ChatConversation from '@/components/ChatConversation.vue'
import ChatMemoryLog from '@/components/ChatMemoryLog.vue'
import GateDecisionActions from '@/components/GateDecisionActions.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import ProposedWorkCard from '@/components/ProposedWorkCard.vue'
import SetupNamesForm from '@/components/SetupNamesForm.vue'
import { CHAT_FILE_UNAVAILABLE, useChatFiles } from '@/composables/useChatFiles'
import { useChatToolCalls } from '@/composables/useChatToolCalls'
import { useUiMode } from '@/composables/useUiMode'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import {
    byLatestMessage,
    type ChatToolItem,
    memoryNote,
    messageGateId,
    messageProposedRunId,
    type RecalledNote,
    recalledNotes,
    replyEndedNote,
    runTaskLabel,
    type ThreadEntry,
    threadEntries,
    toolPayload,
    toolRunId,
} from '@/lib/chat'
import { isFileDrag, namedForAttaching, refusedBeforeUpload, transferFiles } from '@/lib/chatFiles'
import { type SettledGateCard, settledGateCard } from '@/lib/chatGates'
import { exampleTaskRequest } from '@/lib/expressDirectory'
import { gateKind } from '@/lib/gateKind'
import { GUIDED_SETUP_PATH } from '@/lib/guidedSetup'
import { isProviderKeyRefusal, looksLikeProviderKey, PROVIDER_KEY_IN_CHAT_TEXT } from '@/lib/keyShaped'
import { memberLabel } from '@/lib/memberLabel'
import { MORE_TOOLS_PATH, MORE_TOOLS_SKIP, MORE_TOOLS_STEP, MORE_TOOLS_TITLE } from '@/lib/moreTools'
import { ACCOUNT_STEP_PATH } from '@/lib/onboardingGate'
import { onboardingSession } from '@/lib/onboardingSession'
import { errorReason, toolActionLabel } from '@/lib/presentation'
import {
    isSetupChat,
    isWorkFirstRefusal,
    type NamesAnswers,
    namesSaidAs,
    namesTemplateOf,
    offeredKinds,
    PREFER_STEP_BY_STEP,
    type RoleGuess,
    roleGuessFor,
    roleGuessQuestion,
    roleGuessReplies,
    SKIP_SETUP,
    setupChips,
    setupIsOpen,
    setupSkippable,
    TELL_YOUR_COO_VALUE,
    teamProposalReplies,
} from '@/lib/setupChat'
import { connectToolLocation, type TeamSetupView, teamSetupConnectReplies } from '@/lib/teamSetup'

const route = useRoute()
const router = useRouter()
/**
 * #1749: in Standard the thread names a tool by its vendor and action and drops a failed run's error code; Advanced shows the
 * raw tool name and the whole reason, as it always did. Matching (`os.start_task`, a gate's tool) stays on raw names.
 * #1940: a reply's technical Details start folded away in Standard and open in Advanced; a mode switch applies at once.
 */
const { mode: uiMode, setMode } = useUiMode()

/**
 * A tool as this thread names it, in the current mode. The thread is where a founder allows, always-allows and revokes
 * one tool, so Standard names the action as well as the vendor ("GitHub: merge pull request"), never the vendor alone.
 */
function shownTool(toolName: string): string {
    return toolActionLabel(toolName, uiMode.value)
}
/**
 * What a person may attach to one message, stated before any file is chosen (#1731). Built by the shared helper from
 * the one published limits constant, the same figures the API refuses against, so the two cannot drift.
 */
const fileLimits = chatFileLimitsSentence()
const members = ref<Member[]>([])
const sessions = ref<ChatSessionRow[]>([])
const detail = ref<ChatDetail | null>(null)
const draft = ref('')
const error = ref<string | null>(null)
/**
 * #1716: the last thing you tried to send was an AI provider key, stopped here or refused by the API. The composer
 * says so, and where a key goes instead, until you send something else or open another thread. Kept apart from
 * `error`, which a read of the thread clears, so a read landing while the member works does not wipe it at once.
 */
const keyInChat = ref(false)
/**
 * #1732: the files attached for the message being composed, stored by the API and held here by id until the
 * message is sent with them. The button, a drop and a paste all add to it through `attach`, the one way in.
 */
const pendingFiles = ref<ChatAttachedFile[]>([])
/** The API's plain refusal of the last file attached (a kind chat does not take, or over a limit), until the next. */
const attachRefusal = ref<string | null>(null)
/**
 * #2085: a file on its way to the API for the message being composed, each on its own request: `uploading` until it
 * lands (it then joins `pendingFiles`), or `failed` when the network or the server let it down, with Retry.
 */
type Upload = { key: number; chatId: string; file: File; state: 'uploading' | 'failed' }
/**
 * #2085: the uploads not landed yet. Each holds a place in the 5-files limit and holds Send back, so a message never
 * goes out without a file the person thinks is on it, until it lands, or is retried or removed.
 */
const uploads = ref<Upload[]>([])
let uploadKeys = 0
/** Uploads taken off the message while still on their way: one that lands anyway is removed on the server at once. */
const cancelledUploads = new Set<number>()
/**
 * Whether the composer may send: words, or at least one stored file in place of them (#2085), and no file still
 * uploading or failed. Never into a closed thread, or while a tool call is being decided.
 */
const canSend = computed(
    () =>
        !!detail.value &&
        detail.value.status !== 'closed' &&
        !decidingHere.value &&
        uploads.value.length === 0 &&
        (draft.value.trim() !== '' || pendingFiles.value.length > 0),
)
/** A file is being dragged over the conversation: the pane shows it will take the drop. */
const dropping = ref(false)
/** How deep in the pane the drag is: entering a child of the pane leaves the pane itself, which is not leaving it. */
let dragDepth = 0
const attachInput = ref<HTMLInputElement | null>(null)
const starting = ref<string | null>(null)
/** The shared conversation (#244), whose scrolling log the view keeps the reader at the end of. */
const thread = ref<InstanceType<typeof ChatConversation> | null>(null)
/** Every waiting gate in the enterprise; filtered below to the ones this session's runs actually own. */
const gates = ref<GateRow[]>([])
const decidingGateId = ref<string | null>(null)
/** The History panel under the header: every chat with this member, newest first, to reopen one. */
const historyOpen = ref(false)
/** Below md, the member list off-canvas over the thread, opened from the thread's Members trigger. */
const listOpen = ref(false)
/**
 * The tool calls the member asks you about before running them, as Create task draws them too (#244): the call whose
 * Allow / Always allow / Deny is on its way (`decidingCallId`), the cards (`waitingCalls`, oldest first) and what a
 * card shows once decided. A decided call's card fades and closes without waiting on the API; if the API refuses, the
 * card comes back as it was (useChatToolCalls).
 */
const {
    decidingCallId,
    waitingCalls,
    onCard,
    callDecided,
    canDecide,
    decide: decideToolCall,
} = useChatToolCalls(() => detail.value?.messages ?? [])
/**
 * The chat the decision on its way was made in. It is answered only once the member's turn is done, so you may have
 * opened another chat by then; that chat's composer and setup chips do not wait on it, and it is not drawn at work.
 */
const decidingChatId = ref<string | null>(null)
const decidingHere = computed(() => decidingCallId.value !== null && decidingChatId.value === detail.value?.id)
const grantsOpen = ref(false)
const grants = ref<ChatToolGrantRow[]>([])
const revokingGrantId = ref<string | null>(null)
/** Run detail for a linked, succeeded run — read once to render its done card. */
const doneRuns = ref<Record<string, RunDetail>>({})
/**
 * What you have just sent, each drawn at the end of the thread the moment you send it: a send that starts a reply
 * is only answered once the whole reply is done, so without this your message would land together with the
 * answer to it. You can write again while the member works (#1356), so there can be several. `after` is how many
 * messages from people the thread held when you sent it, counting the ones sent just before it that it does not
 * hold yet; once the server's copy is in, it holds one more, and that copy is the one drawn. `waiting` is a message
 * sent while the member was at work: it waits for the member to read it, and says so at once.
 */
type Outgoing = { key: string; chatId: string; text: string; after: number; waiting: boolean }
const outbox = ref<Outgoing[]>([])
let sends = 0
/** What is on its way in the chat on screen: a send in a chat since left is drawn there, and only there. */
const outboxHere = computed(() => outbox.value.filter(sent => sent.chatId === detail.value?.id))
/** A send is on its way to the API in the chat on screen. */
const sending = computed(() => outboxHere.value.length > 0)
/**
 * What was typed and not sent in each chat left, by its id: the composer is the view's one textarea, so opening another
 * chat puts away what it held and brings back what that chat's held.
 */
const drafts = new Map<string, string>()
/** You, as the thread draws what you write: what you just sent carries your avatar before the server's copy lands. */
const you = ref<SurfaceMember | null>(null)
/** Your principal (`human:<id>`), to tell a thread you own from one you opened by its link. Null until known. */
const yourPrincipal = ref<string | null>(null)
/** The thread's link was just copied: the action says so for a moment. */
const linkCopied = ref(false)
let linkCopiedTimer: ReturnType<typeof setTimeout> | null = null
/**
 * Every read of the thread takes a number, and the newest to land is remembered, so a read that a newer one
 * overtook (a poll still in flight when your send's answer landed) is dropped rather than drawn over it.
 */
let reads = 0
let landed = 0
/** The `loadGates` read under way, if any, which every call made meanwhile shares (#1981). */
let gatesLoading: Promise<boolean> | null = null
let timer: ReturnType<typeof setInterval> | null = null
let listTimer: ReturnType<typeof setInterval> | null = null

/**
 * The linked run behind the most recent `tool` message with a `runId`: chat's own `status` clears
 * the instant the LLM turn hands work off, right when the run (possibly minutes long) is only
 * getting started, so the indicator is driven off this run's own live status instead — restorable on a
 * fresh mount from `messages` alone, with no client-only flag. It never shuts the composer (#1356).
 */
const trackedRunId = ref<string | null>(null)
const trackedRun = ref<RunDetail | null>(null)
let runTimer: ReturnType<typeof setInterval> | null = null
let clockTimer: ReturnType<typeof setInterval> | null = null
const now = ref(Date.now())

/**
 * The page's one plain sentence (#1762, D7), under the title and again as the chat-list sheet's description on a
 * narrow screen, so both read the same. It says what Chat is for, not how the engine runs it.
 */
const CHAT_INTRO = 'Talk to your teammates. Ask for work, get answers, decide in the thread.'

/** A linked run busy enough to keep the thread saying so. */
const ACTIVE_RUN_STATUSES = new Set(['pending', 'queued', 'running', 'waiting_on_run', 'paused_spend'])

const sessionId = computed(() => (typeof route.params.id === 'string' ? route.params.id : ''))

/**
 * #1738: the setup conversation's saved `work_profile`, read from the setup state when the conversation opens and
 * taken from each save after it. Undefined until read: no chips are drawn before it is known which question is open.
 */
const setupSaved = ref<unknown>(undefined)
/** A setup answer is on its way to the setup API: the chips, and a send, wait on it. */
const setupSaving = ref(false)

/** The setup question the open conversation is on: kind of work, then role, then how technical. Null elsewhere. */
const setupOpen = computed((): SetupQuestion | null =>
    isSetupChat(detail.value) && setupSaved.value !== undefined ? setupQuestion(setupSaved.value) : null,
)

/**
 * #1742: the setup state the conversation last read or saved, for what it offers around the questions: "Prefer
 * step-by-step?" while setup is open, and the skip link once the AI account step is complete. Null until read.
 */
const setupState = ref<OnboardingStateView | null>(null)
/** The setup path is being switched, or setup skipped: the links wait on it. */
const setupMoving = ref(false)
const offerStepByStep = computed(() => isSetupChat(detail.value) && setupIsOpen(setupState.value))
const offerSkip = computed(() => isSetupChat(detail.value) && setupSkippable(setupState.value))

/**
 * #1742 (O13): a typed role waiting for the person to confirm the known role it matched. Nothing is saved on the
 * guess: the COO's confirmation is drawn at the end of the thread, with chips to accept it or pick another role.
 */
const roleGuess = ref<RoleGuess | null>(null)
/**
 * The files attached with that typed role (#2085), and the chat they were attached in (review of PR #2065). A typed
 * setup answer carries its files (#1738), so they leave the composer with the typed words and go with whichever answer
 * confirms the role: never left behind to go with a later message. A guess that is dropped puts them back.
 */
const roleGuessFiles = ref<{ chatId: string; files: ChatAttachedFile[] } | null>(null)

/** The chips of that question, drawn with the shared quick replies; null when no setup question is open. */
const setupReplyChips = computed(() =>
    roleGuess.value && setupOpen.value === 'role'
        ? { replies: roleGuessReplies(roleGuess.value), otherLabel: null }
        : setupChips(setupOpen.value, detail.value?.messages ?? []),
)

/** The first thing the COO said in the thread: the skip link sits under it (#1742). */
const firstReplyKey = computed(() => entries.value.find(entry => entry.kind === 'assistant')?.id ?? null)

/**
 * The text typed under the setup chips' "Something else" (#1738). A refused save keeps it, as the composer keeps its
 * draft; it is cleared when the question moves on (the answer was taken, or another conversation opened), so a typed
 * answer begun under one question never carries into the next.
 */
const setupDraft = ref('')
watch(setupOpen, () => {
    setupDraft.value = ''
    roleGuess.value = null
    // #1742: the files held with a typed role no longer asked about go back in the composer, so none is lost.
    returnToComposer(sessionId.value, takeRoleGuessFiles())
})

/**
 * #1739: what the cloned team still needs, as the setup state served this conversation, read when it opens. The chat
 * offers Connect for the current item alone (`teamSetupConnectReplies`), the same item the guided screen shows.
 */
const setupTeam = ref<TeamSetupView | null>(null)
const setupConnectReplies = computed(() => (isSetupChat(detail.value) ? teamSetupConnectReplies(setupTeam.value) : []))

/**
 * #2216: the two steps between the work profile and the assembly, answered under the conversation. At `team_proposal`
 * the teams the setup state proposes are chips (`teamProposalReplies`), the same teams the COO presents; at `names`
 * the proposed team's template is the names form, the one the guided Names screen draws. Each save goes through the
 * same setup API as the work profile, then what was chosen is sent as the person's message, so the COO's turn runs.
 */
const setupProposalReplies = computed(() => (isSetupChat(detail.value) ? teamProposalReplies(setupState.value) : []))
const setupNamesTemplate = computed(() => (isSetupChat(detail.value) ? namesTemplateOf(setupState.value) : null))
/** The server's plain-words refusal of the names last saved, shown on the form, which keeps what was typed. */
const setupNamesFailure = ref<string | null>(null)

/**
 * #1743: the enterprise's assembly, whose log of real setup events the conversation shows from the `assembly` step
 * on, the same log guided screen 5 shows. Null outside the setup conversation, before the assembly has started, and
 * once setup has moved past team setup.
 */
const setupAssemblyId = computed(() => {
    const state = setupState.value
    if (!isSetupChat(detail.value) || !state?.assemblyId) {
        return null
    }
    return state.currentStep === 'assembly' || state.currentStep === 'team_setup' ? state.assemblyId : null
})

/** The assembly finished: setup's step may have moved on to team setup, so where setup stands is read again. */
function reloadSetup(): void {
    if (detail.value) {
        void loadSetup(detail.value)
    }
}

/** The setup conversation's Connect chip: the existing Tools connect flow, opened on that tool type. */
function connectSetupTool(toolType: string): void {
    void router.push(connectToolLocation(toolType))
}

/**
 * #1942: once Team setup is saved, the server offers the optional tools step until it is skipped or done. The
 * conversation then offers a button that opens the same step screen the guided path shows, and a Skip that saves the
 * same server-held skip.
 */
const offerMoreTools = computed(() => isSetupChat(detail.value) && Boolean(setupState.value?.moreTools))

function openMoreTools(): void {
    void router.push(MORE_TOOLS_PATH)
}

/** Skip on the optional tools step: saved in the setup state, whose answer is read in so the chat moves on. */
async function skipMoreTools(): Promise<void> {
    const chat = detail.value
    if (!chat) {
        return
    }
    setupMoving.value = true
    try {
        takeSetupState(chat.id, await api.saveOnboardingStep(MORE_TOOLS_STEP, { skipped: true }, 'chat', chat.id))
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not skip that step.'
    } finally {
        setupMoving.value = false
    }
}

/**
 * You opened somebody else's thread by its link (#1262): you can read it, and write in it with the grant, but it
 * is theirs. It is never written into your list or History, and only its owner can close it. Not said of a thread
 * nobody owns, nor until both owner and you are known, so an owner never sees it flash up.
 */
const guest = computed(() => {
    const owner = detail.value?.ownerPrincipal
    return !!owner && yourPrincipal.value !== null && owner !== yourPrincipal.value
})

/**
 * Oldest first, as a conversation reads, with the composer under it: the thread opens at its end. A call drawn as
 * a card stays out of the log until the card has gone.
 */
const threadRows = computed(() => threadEntries((detail.value?.messages ?? []).filter(row => !onCard(row))))

/**
 * The thread as the shared drawing takes it (ZhChatThread). A person's message carries who wrote it, since
 * several people can share a thread; everything else the member said is theirs, and a turn where it only
 * worked is a reply with no words, its log under it.
 */
const entries = computed((): ChatEntry[] => {
    const drawn = threadRows.value.map((entry): ChatEntry => {
        if (entry.kind === 'tools') {
            return { kind: 'assistant', id: entry.key, text: '' }
        }
        const text = entry.row.content ?? ''
        // #283: how work the member started ended is a line from the OS, drawn on its own row with its run's link.
        if (outcomeRunOf(entry.row)) {
            return { kind: 'system', id: entry.key, text }
        }
        return entry.row.role === 'user'
            ? { kind: 'user', id: entry.key, text, sender: entry.row.sender ?? null }
            : { kind: 'assistant', id: entry.key, text }
    })
    const guess = roleGuess.value
    return [
        ...drawn,
        ...unlanded().map((sent): ChatEntry => ({ kind: 'user', id: sent.key, text: sent.text, sender: you.value })),
        // #1742: a typed role, and the COO's confirmation of the role it matched, until it is confirmed.
        ...(guess
            ? [
                  { kind: 'user', id: 'setup-role-typed', text: guess.typed, sender: you.value } as ChatEntry,
                  { kind: 'assistant', id: 'setup-role-guess', text: roleGuessQuestion(guess) } as ChatEntry,
              ]
            : []),
    ]
})

/**
 * #283: the run an outcome entry reports, or null for any other row. An outcome entry is a `system` row whose
 * payload names how the work ended and the run that ended it: `<task name> · <outcome>`, with "Open run" beside it.
 */
function outcomeRunOf(row: ChatMessageRow): string | null {
    const runIdOf = row.payload?.['runId']
    return row.role === 'system' && typeof row.payload?.['outcome'] === 'string' && typeof runIdOf === 'string'
        ? runIdOf
        : null
}

/** Each outcome entry's run, by its entry's key: where its "Open run" goes. */
const outcomeRuns = computed(
    () =>
        new Map(
            threadRows.value.flatMap((entry): [string, string][] => {
                const run = entry.kind === 'message' ? outcomeRunOf(entry.row) : null
                return run ? [[entry.key, run]] : []
            }),
        ),
)

/** What you have sent that the thread does not hold a copy of yet, oldest first. */
function unlanded(): Outgoing[] {
    const held = yourMessageCount()
    return outboxHere.value.filter(sent => held <= sent.after)
}

/**
 * The messages drawn as waiting for the member to read them, by entry key: the API's mark on each, and what you
 * have just sent while it was at work, until the server's copy, with its own mark, is in.
 */
const waitingKeys = computed(
    () =>
        new Set([
            ...(detail.value?.messages ?? []).filter(row => row.role === 'user' && row.waiting).map(row => row.id),
            ...unlanded()
                .filter(sent => sent.waiting)
                .map(sent => sent.key),
        ]),
)

/**
 * #284: the waiting messages you may take back, by entry key, as the API marks them (`withdrawable`): the sender's
 * own, or any for whoever may end the thread. The portal never judges that itself. What you have just sent has no
 * mark until the server's copy lands, so it offers no Withdraw before then.
 */
const withdrawableKeys = computed(
    () =>
        new Set(
            (detail.value?.messages ?? [])
                .filter(row => row.role === 'user' && row.waiting && row.withdrawable)
                .map(row => row.id),
        ),
)

/**
 * The member is at work on a reply: from the moment you send, or answer a tool call it asked about, until the reply
 * lands, or in a thread reopened mid-turn. A turn waiting on your answer is not one: it is handed back idle.
 */
const replying = computed(() => sending.value || decidingHere.value || detail.value?.status === 'replying')

/**
 * The member's own step list for the thread's current request (#286), from the thread read the view already makes:
 * nothing at all for a thread with no list. Drawn just above the composer, outside the scrolling messages. The member
 * only moves it during a reply, and the view already reads the thread every second while one is under way, so the
 * list moves without a reload and adds no read of its own (#286 decision 8: no new endpoint, push channel or poll).
 */
const steps = computed(() => detail.value?.steps ?? [])

/** At Tailwind's md and up the list is drawn whole; below it, on a phone, it starts as its one-line summary. */
const wideScreen = useMediaQuery('(min-width: 768px)')

/**
 * Which state an open chat is in, derived once so exactly one is drawn (#974). `error` beats `empty` and
 * `empty` beats `loading`: a read that failed is `error` whether or not a thread had loaded before it (a
 * loaded thread stays on screen under the message), and `loading` only lasts until the first read lands.
 * `empty` is a thread read with nothing in it and the member not at work on a reply; everything else is
 * `thread`. Null with no chat open.
 */
const viewState = computed((): 'loading' | 'empty' | 'error' | 'thread' | null => {
    if (!sessionId.value) {
        return null
    }
    if (error.value) {
        return 'error'
    }
    if (!detail.value) {
        return 'loading'
    }
    return entries.value.length === 0 && !replying.value ? 'empty' : 'thread'
})

/** The loading thread's stand-in messages: theirs on the left, yours on the right, as the thread draws them. */
const LOADING_BUBBLES = [
    { key: 'a1', class: 'h-14 w-2/3 self-start' },
    { key: 'u1', class: 'h-10 w-1/2 self-end' },
    { key: 'a2', class: 'h-20 w-3/5 self-start' },
] as const

/** The tool rows behind each entry, by its key: what `message-extra` shows under the reply. */
const toolsByKey = computed(() => new Map(threadRows.value.map(entry => [entry.key, entry.tools])))

/**
 * #1734: the files each message carried, oldest first, by its entry's key: the chips `message-extra` draws under it.
 * A message that carried none has no entry here.
 */
const filesByKey = computed(
    () =>
        new Map(
            threadRows.value.flatMap((entry): [string, ChatAttachedFile[]][] =>
                entry.kind === 'message' && entry.row.files?.length ? [[entry.key, entry.row.files]] : [],
            ),
        ),
)

/**
 * The bytes behind the thread's chips (#1734), read through the api client for the chat on screen: each image's
 * thumbnail, the file in the preview dialog, and a file opened in a new tab or saved. Revoked as each goes.
 */
const {
    thumbnails: fileThumbnails,
    unavailable: unavailableFiles,
    preview: filePreview,
    loadThumbnails,
    openPreview: previewFile,
    closePreview: closeFilePreview,
    openInTab: openFile,
    download: downloadFile,
    reset: resetFiles,
} = useChatFiles(() => detail.value?.id ?? sessionId.value)

/** Every file a message in the thread carried: an image among them is read for its thumbnail once. */
watch(
    () => (detail.value?.messages ?? []).flatMap(row => row.files ?? []),
    files => loadThumbnails(files),
    { immediate: true },
)

/** The preview dialog was closed (its Close button, Escape or a click outside): its file's URL goes with it. */
function onFilePreviewOpen(open: boolean): void {
    if (!open) {
        closeFilePreview()
    }
}

/** How a reply ended when it did not simply finish, by its entry's key: the line `message-extra` shows under it. */
const endedByKey = computed(
    () =>
        new Map(
            threadRows.value.flatMap((entry): [string, string][] => {
                const note = entry.kind === 'message' ? replyEndedNote(entry.row) : null
                return note ? [[entry.key, note]] : []
            }),
        ),
)

/** What memory recalled for a person's message, by its entry's key: the line `message-extra` shows under it. */
const memoryByKey = computed(
    () =>
        new Map(
            threadRows.value.flatMap((entry): [string, { summary: string; notes: RecalledNote[] }][] => {
                const summary = entry.kind === 'message' ? memoryNote(entry.row) : null
                return summary && entry.kind === 'message'
                    ? [[entry.key, { summary, notes: recalledNotes(entry.row) }]]
                    : []
            }),
        ),
)

/** When an entry happened: its message, or the last tool call of a turn that has no reply yet. */
function entryTime(entry: ThreadEntry): number {
    const row = entry.kind === 'message' ? entry.row : entry.tools.at(-1)
    return row ? new Date(row.createdAt).getTime() : 0
}

/**
 * A gate's card in the thread: one still waiting on you, with its controls (`waiting`), or one decided, wherever it
 * was decided, showing how it ended for good (`settled`, #1981).
 */
type GateCard = {
    id: string
    createdAt: string
    decision: DecisionView
    waiting?: GateRow
    settled?: SettledGateCard
}

/**
 * Every gate card the thread draws, oldest first: each waiting gate it carries, and each gate it carries that the
 * waiting list no longer has and the thread's read says has ended. A gate still in the waiting list is drawn waiting,
 * with its controls; once it leaves, the thread is read again (`loadGates`) and its card shows the outcome instead,
 * with none.
 */
const gateCards = computed((): GateCard[] => {
    const waiting = pendingGates.value.map(
        (gate): GateCard => ({ id: gate.id, createdAt: gate.createdAt, decision: gateDecision(gate), waiting: gate }),
    )
    const shown = new Set(waiting.map(card => card.id))
    const settled = (detail.value?.gates ?? []).flatMap((gate): GateCard[] => {
        const card = shown.has(gate.id) ? null : settledGateCard(gate, now.value)
        return card ? [{ id: gate.id, createdAt: gate.createdAt, decision: gateDecision(gate), settled: card }] : []
    })
    return [...waiting, ...settled].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
})

/**
 * Each gate's card, under the entry it follows: the last one at or before the moment the gate opened, else the
 * first. A card stays where the conversation was when it was raised, decided or not, and anything said after it is
 * drawn below it, rather than every card sitting pinned at the end of the thread.
 */
const gatesByKey = computed(() => {
    const byKey = new Map<string, GateCard[]>()
    const rows = threadRows.value
    for (const card of gateCards.value) {
        const opened = new Date(card.createdAt).getTime()
        const anchor = rows.filter(entry => entryTime(entry) <= opened).at(-1) ?? rows[0]
        if (anchor) {
            byKey.set(anchor.key, [...(byKey.get(anchor.key) ?? []), card])
        }
    }
    return byKey
})

/**
 * The member directory as the list draws it: their role, the last thing said, and how many of their replies
 * you have not read. That count is across every chat with them, so the list adds up to the nav's badge.
 * Newest message first (`byLatestMessage`), and both the desktop list and the off-canvas one draw this, so a
 * refresh that brings a reply moves its member to the top of each.
 */
const people = computed((): ChatListItem[] =>
    byLatestMessage(members.value, sessions.value).map(member => ({
        id: member.id,
        // Labelled as everywhere else (#1815): the unnamed COO's row reads "COO", not a blank.
        member: { ...member, name: memberLabel(member) },
        role: roleOf(member.id),
        last: preview(latestFor(member.id)),
        open: detail.value?.memberId === member.id,
        unread: sessions.value
            .filter(session => session.memberId === member.id)
            .reduce((total, session) => total + unreadIn(session), 0),
    })),
)

/**
 * The member the open thread is with, labelled as everywhere else in the portal (#1815): the COO an enterprise is
 * made with is unnamed and reads "COO" in the thread's head and copy, not a blank.
 */
const threadMember = computed(() =>
    detail.value ? { ...detail.value.member, name: memberLabel(detail.value.member) } : null,
)

/** Every chat with the member you are talking to, as the API orders them: most recently touched first. */
const memberHistory = computed(() => sessions.value.filter(session => session.memberId === detail.value?.memberId))

const indicatorActive = computed(() => {
    const run = trackedRun.value?.run
    return !!run && ACTIVE_RUN_STATUSES.has(run.status)
})

/** The linked run's live status, so the indicator can say which kind of busy it is. Null when nothing is tracked. */
const trackedStatus = computed(() => trackedRun.value?.run.status ?? null)

/**
 * What the Stop control says beside it (#281) while a run started from the thread is still working, linking each:
 * a stop never touches a run, and a run page has Cancel. With no run working it points nowhere, so it is left out.
 */
const STOP_NOTE = 'Work already started carries on. Cancel it from its run page.'

/** A Stop pressed here, on its way: the control reads "Stopping…", disabled, until the thread read shows it stopped. */
const stopping = ref(false)

/**
 * Stop (#281) shows while the member is replying. Once the thread is idle, closed, or stopped and idle, there is no
 * reply to stop, and it goes. (The member carrying on unprompted, #283, will show it too.)
 */
const stopShown = computed(() => detail.value?.status === 'replying')

/** Pressed, here or by someone else: the thread read names who stopped it while the reply finishes its step. */
const stopPending = computed(() => stopping.value || Boolean(detail.value?.stoppedBy))

/** Each run started from this thread that is still working, once, for the stop note to link (#281). */
const activeThreadRuns = computed((): { id: string; label: string }[] => {
    const active = new Map<string, string>()
    for (const row of detail.value?.messages ?? []) {
        const id = row.role === 'tool' ? runId(row) : null
        if (id && ACTIVE_RUN_STATUSES.has(runStatus(row))) {
            active.set(id, row.run?.taskName ?? 'Run')
        }
    }
    return [...active].map(([id, label]) => ({ id, label }))
})

/** A Stop the API refused (#281), said beside the control until it is pressed again or the reply ends. */
const stopRefused = ref(false)
const STOP_REFUSED = 'Could not stop the reply. Try again.'

/** Said while a stop is on its way (#281), and announced to screen readers: it bites once the current step ends. */
const STOPPING_NOTE = 'It stops once the step it is on ends.'

/** Under a stopped thread (#281): nothing moves until someone writes, and the next message lifts the stop. */
const STOPPED_HINT = 'Stopped. Send a message to carry on.'
const stoppedHintShown = computed(() => detail.value?.status === 'idle' && Boolean(detail.value?.stoppedBy))

// A reply that has ended, or another thread opened, takes its Stop with it: the next Stop starts unpressed, with no
// refusal left over from an earlier one (ChatView is reused from one chat to the next).
watch([() => detail.value?.id, () => detail.value?.status], ([id, status], [previousId]) => {
    if (id !== previousId || status !== 'replying') {
        stopping.value = false
        stopRefused.value = false
    }
})

/** Stops the member at once (#281): no confirmation, since a stop loses nothing and the next message undoes it. */
async function stopReply(): Promise<void> {
    const chat = detail.value
    if (!chat || stopPending.value) {
        return
    }
    stopping.value = true
    stopRefused.value = false
    try {
        await api.stopChat(chat.id)
    } catch {
        // Said beside the control, in words, and Stop can be pressed again: the reply is still under way. Only on the
        // thread it was pressed in: a refusal for a chat left meanwhile says nothing in the one open now.
        if (detail.value?.id === chat.id) {
            stopping.value = false
            stopRefused.value = true
        }
    }
}

/**
 * A linked run waiting for a slot rather than working (os#238). Two conversations of the same kind can now run at
 * once, but one can still queue — behind the enterprise's or team's running-task ceiling, or behind another run of
 * an ordinary task — and a queued run has no `startedAt`, so the working state's elapsed counter would sit at 0s
 * and read as stalled. Say it is queued instead.
 */
const indicatorQueued = computed(() => trackedStatus.value === 'queued' || trackedStatus.value === 'pending')

const trackedElapsedMs = computed<number | null>(() => {
    const startedAt = trackedRun.value?.run.startedAt
    return startedAt ? Math.max(0, now.value - new Date(startedAt).getTime()) : null
})

/** The run's most recent event, so the step line moves as the run does. */
const trackedStep = computed(() => {
    const events = trackedRun.value?.events ?? []
    if (events.length === 0) {
        return null
    }
    return [...events].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
})

/**
 * #849: a lead's message putting a run's proposed work to you, by its entry's key: the run, and its batch as last read,
 * null once the last item is decided, wherever it was. Nothing is drawn under a message until the batches are read.
 */
const proposedBatches = ref<ProposedWorkBatch[] | null>(null)
const proposedByKey = computed(() => {
    const byKey = new Map<string, { runId: string; batch: ProposedWorkBatch | null }>()
    const read = proposedBatches.value
    if (read === null) {
        return byKey
    }
    for (const entry of threadRows.value) {
        const proposing = entry.kind === 'message' ? messageProposedRunId(entry.row) : null
        if (proposing) {
            byKey.set(entry.key, {
                runId: proposing,
                batch: read.find(batch => batch.runId === proposing) ?? null,
            })
        }
    }
    return byKey
})

/** How often a thread that carries proposed work reads it again: an item can be decided on the board meanwhile. */
const PROPOSED_READ_MS = 5000
let proposedReadAt = 0

/** Reads the proposed work again, only for a thread that carries some, and at most every `PROPOSED_READ_MS`. */
async function loadProposed(fresh = false): Promise<void> {
    const carries = (detail.value?.messages ?? []).some(row => messageProposedRunId(row) !== null)
    if (!carries || (!fresh && Date.now() - proposedReadAt < PROPOSED_READ_MS)) {
        return
    }
    proposedReadAt = Date.now()
    try {
        proposedBatches.value = await api.proposedWork()
    } catch {
        // The cards stay as they were last read; the next read tries again.
    }
}

/** A card's answer: its batch as it now stands replaces the one read, or leaves the list once it has closed. */
function proposedDecided(proposing: string, batch: ProposedWorkBatch | null): void {
    const others = (proposedBatches.value ?? []).filter(candidate => candidate.runId !== proposing)
    proposedBatches.value = batch ? [...others, batch] : others
}

/** Runs this session's own messages started, so a waiting gate on any of them belongs in this thread. */
const sessionRunIds = computed((): Set<string> => {
    const ids = (detail.value?.messages ?? []).map(runId).filter((id): id is string => id !== null)
    return new Set(ids)
})

/**
 * The gates this thread was asked about directly (#229): a run's CEO question is posted into its member's
 * thread carrying `payload.gateId`, by a run this session never started — so it is named here, not by
 * `sessionRunIds`. Without this the question renders as text the CEO cannot answer without the gates page.
 */
const chatGateIds = computed((): Set<string> => {
    const ids = (detail.value?.messages ?? []).map(messageGateId).filter((id): id is string => id !== null)
    return new Set(ids)
})

/** Every waiting gate this thread either started (a run of its own) or was asked about, oldest first. */
const pendingGates = computed((): GateRow[] => {
    const runIds = sessionRunIds.value
    const gateIds = chatGateIds.value
    return gates.value
        .filter(candidate => runIds.has(candidate.runId) || gateIds.has(candidate.id))
        .slice()
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
})

function yourMessageCount(): number {
    return (detail.value?.messages ?? []).filter(row => row.role === 'user').length
}

/**
 * Whether a send that failed was kept all the same. A reply can outlast the connection that sent it: the API has
 * the message and the member is still at work. Read afresh, the thread then holds `text` past the `after` messages
 * of yours it had when you sent.
 */
async function sentAnyway(id: string, text: string, after: number): Promise<boolean> {
    if (id === sessionId.value) {
        await loadSession()
    }
    // A chat left meanwhile is read on its own, never drawn: the one on screen is another's.
    const chat = detail.value?.id === id ? detail.value : await api.chat(id).catch(() => null)
    const yours = (chat?.messages ?? []).filter(row => row.role === 'user')
    return yours.slice(after).some(row => row.content?.trim() === text)
}

/** A member's first role, as the list and the thread's head label them; a member with none is a team member. */
function roleOf(memberId: string): string {
    return members.value.find(member => member.id === memberId)?.roles?.[0]?.name ?? 'Team member'
}

function selectPerson(item: ChatListItem): void {
    const member = members.value.find(candidate => candidate.id === item.id)
    if (member) {
        void openMember(member)
    }
}

/** A pick from the off-canvas list puts the thread back in front, even when it is the member already open. */
function selectFromSheet(item: ChatListItem): void {
    listOpen.value = false
    selectPerson(item)
}

/** A gate, waiting or decided, as the thread's decision card heads it: the task it is for, and the tool it runs. */
function gateDecision(gate: Pick<GateRow, 'taskName' | 'taskSlug' | 'toolName'>): DecisionView {
    return { title: gateTaskLabel(gate), why: shownTool(gate.toolName) }
}

/** How close to its end the thread counts as being there: new messages then keep it there. */
const END_SLACK_PX = 48

function atEnd(): boolean {
    const log = thread.value?.log
    return !log || log.scrollHeight - log.scrollTop - log.clientHeight <= END_SLACK_PX
}

function scrollToEnd(): void {
    const log = thread.value?.log
    log?.scrollTo({ top: log.scrollHeight })
}

/**
 * The first reply you have not read, by its entry key: the first entry not yours that postdates when you
 * last opened the thread. Read from the list row, since opening the thread moves `lastReadAt` to its end.
 */
function firstUnreadKey(listed: ChatSessionRow | undefined): string | null {
    if (!listed?.unread) {
        return null
    }
    const since = listed.lastReadAt
    for (const entry of threadRows.value) {
        const row = entry.kind === 'message' ? entry.row : entry.tools[0]
        if (row && row.role !== 'user' && (!since || row.createdAt > since)) {
            return entry.key
        }
    }
    return null
}

/** Opening a thread lands on the first reply you have not read, and otherwise on its newest message. */
function land(listed: ChatSessionRow | undefined): void {
    const key = firstUnreadKey(listed)
    const target = key ? thread.value?.log?.querySelector(`[data-entry-id="${key}"]`) : null
    if (target) {
        target.scrollIntoView({ block: 'start' })
    } else {
        scrollToEnd()
    }
}

function latestFor(memberId: string): ChatSessionRow | undefined {
    return sessions.value.find(session => session.memberId === memberId && session.status !== 'closed')
}

/** The most recent `tool` message that links a run, scanning from the newest message backwards. */
function latestRunMessage(): ChatMessageRow | null {
    const rows = detail.value?.messages ?? []
    for (let index = rows.length - 1; index >= 0; index -= 1) {
        const row = rows[index]
        if (row.role === 'tool' && runId(row)) {
            return row
        }
    }
    return null
}

/**
 * Points tracking at whatever run the thread's newest tool message links, restoring it on a fresh
 * mount from `messages` alone. A no-op when it is already tracking that run, so the 1s poll below
 * does not restart on every `loadSession()`.
 */
function syncTrackedRun(): void {
    const message = latestRunMessage()
    const id = message ? runId(message) : null
    if (id === trackedRunId.value) {
        return
    }
    trackedRunId.value = id
    trackedRun.value = null
    if (id) {
        void fetchTrackedRun(id)
    }
}

async function fetchTrackedRun(id: string): Promise<void> {
    try {
        const result = await api.run(id)
        if (trackedRunId.value === id) {
            trackedRun.value = result
        }
    } catch {
        // The run may not exist yet (or ever again); keep the last-known state rather than flicker.
    }
}

onMounted(async () => {
    void loadYou()
    await loadDirectory()
    if (!(await openSetupChat()) && !(await openTaskChat()) && !(await openLatestSession())) {
        await loadSession()
    }
    await loadGates()
    timer = setInterval(() => {
        void loadGates()
        void loadDoneRuns()
        void loadProposed()
        // Read while the member replies, so the tool calls it makes show up as it makes them.
        if (replying.value) {
            void loadSession()
        }
    }, 1000)
    listTimer = setInterval(() => {
        void refreshList()
    }, 5000)
    runTimer = setInterval(() => {
        if (trackedRunId.value) {
            void fetchTrackedRun(trackedRunId.value)
        }
    }, 1000)
    clockTimer = setInterval(() => {
        now.value = Date.now()
    }, 250)
})

onUnmounted(() => {
    if (timer) {
        clearInterval(timer)
    }
    if (listTimer) {
        clearInterval(listTimer)
    }
    if (runTimer) {
        clearInterval(runTimer)
    }
    if (clockTimer) {
        clearInterval(clockTimer)
    }
    if (linkCopiedTimer) {
        clearTimeout(linkCopiedTimer)
    }
})

watch(sessionId, (id, previous) => {
    // What was typed stays with its chat. Arriving from no chat (a purpose link opening its own) keeps the composer as is.
    if (previous) {
        drafts.set(previous, draft.value)
        draft.value = drafts.get(id) ?? ''
        drafts.delete(id)
    }
    // The chat left is not drawn under the one opened while it loads: its thread, its run and its error go with it,
    // and nothing typed meanwhile can be sent into it.
    detail.value = null
    error.value = null
    syncTrackedRun()
    historyOpen.value = false
    listOpen.value = false
    keyInChat.value = false
    pendingFiles.value = []
    uploads.value = []
    resetFiles()
    attachRefusal.value = null
    // #284: a refused withdrawal's reason belongs to the chat it happened in, and is gone once you leave it.
    withdrawNotice.value = null
    setupSaved.value = undefined
    setupTeam.value = null
    setupState.value = null
    setupNamesFailure.value = null
    roleGuess.value = null
    // Still waiting on the server unsent, so the chat they were attached in puts them back when it opens again.
    roleGuessFiles.value = null
    // #849: the thread opened may carry proposed work: it is read at the next tick, not five seconds on.
    proposedReadAt = 0
    void loadSession()
    void loadGates()
})

/**
 * `/chat?purpose=company_setup` opens a new setup conversation with the COO (#1738), its greeting and first chips
 * already in it, and replaces the address with the conversation's own, so a reload reopens that conversation rather
 * than starting another. True when it took the navigation over, refused or not.
 */
async function openSetupChat(): Promise<boolean> {
    if (sessionId.value || route.query.purpose !== SETUP_CHAT_PURPOSE) {
        return false
    }
    try {
        const opened = await api.openChatForPurpose(SETUP_CHAT_PURPOSE)
        await router.replace(`/chat/${opened.id}`)
    } catch {
        error.value = 'Could not start setting up your company. Try again.'
    }
    return true
}

/**
 * `/chat?purpose=create_task` opens a new create-a-task conversation with the COO (#1203), and replaces the address
 * with the conversation's own, as setup's does. With `tool=<tool type>` it also writes that Express tool's example
 * task into the message box as a request. The words are read from the tool's own record, never from the address,
 * and nothing is sent: the person reads it, changes what they like and sends it themselves. True when it took the
 * navigation over, refused or not.
 */
async function openTaskChat(): Promise<boolean> {
    if (sessionId.value || route.query.purpose !== CREATE_TASK_CHAT_PURPOSE) {
        return false
    }
    const tool = typeof route.query.tool === 'string' ? route.query.tool : ''
    try {
        const [opened, request] = await Promise.all([
            api.openChatForPurpose(CREATE_TASK_CHAT_PURPOSE),
            exampleRequestOf(tool),
        ])
        await router.replace(`/chat/${opened.id}`)
        if (request && !draft.value) {
            draft.value = request
        }
    } catch {
        error.value = 'Could not start a conversation to create a task. Try again.'
    }
    return true
}

/** The request an Express tool's example task makes, or null: for no tool, one with no example, or a failed read. */
async function exampleRequestOf(slug: string): Promise<string | null> {
    if (!slug) {
        return null
    }
    try {
        const tool = (await api.expressTools()).find(each => each.slug === slug)
        return tool ? exampleTaskRequest(tool) : null
    } catch {
        return null
    }
}

/** Where the setup conversation's setup stands (#1738), read once it opens, so the open question's chips show. */
async function loadSetup(chat: ChatDetail): Promise<void> {
    if (!isSetupChat(chat)) {
        return
    }
    try {
        takeSetupState(chat.id, await api.onboarding('chat', chat.id))
    } catch {
        if (chat.id === sessionId.value) {
            error.value = 'Could not read where setup is.'
        }
    }
}

/**
 * A setup state the conversation read or saved, taken in whole while that conversation is still the one on screen:
 * the saved work profile (which question is open), what the cloned team needs, and the state itself (what the other
 * steps offer). A state that arrives for a conversation since left is dropped.
 */
function takeSetupState(chatId: string, state: OnboardingStateView): void {
    if (chatId === sessionId.value) {
        setupSaved.value = state.answers['work_profile'] ?? null
        setupTeam.value = state.teamSetup ?? null
        setupState.value = state
    }
}

/**
 * Landing on /chat opens the conversation you were last in, so the common case costs no click. The
 * member list stays beside it on desktop and behind the Members trigger on a phone. True when it navigated; the
 * sessionId watcher then loads the thread.
 */
async function openLatestSession(): Promise<boolean> {
    if (sessionId.value) {
        return false
    }
    const latest = sessions.value.find(session => session.status !== 'closed')
    if (!latest) {
        return false
    }
    await router.replace(`/chat/${latest.id}`)
    return true
}

async function loadYou(): Promise<void> {
    try {
        const me = await api.me()
        if (me) {
            you.value = {
                name: me.user.name?.trim() || me.user.githubLogin || 'You',
                avatarUrl: me.user.avatarUrl ?? null,
            }
            yourPrincipal.value = `human:${me.user.id}`
        }
    } catch {
        // Without it, what you just sent takes your avatar once the server's copy lands.
    }
}

async function loadDirectory(): Promise<void> {
    try {
        const [memberRows, chatRows] = await Promise.all([api.members(), api.chats()])
        members.value = memberRows
        sessions.value = chatRows
    } catch {
        error.value = 'Could not load chat.'
    }
}

/**
 * The list keeps up with replies landing while you are here, on the nav badge's own beat, so the two agree.
 * A reply landing in the thread on screen is drawn, which reads it, rather than counted.
 */
async function refreshList(): Promise<void> {
    try {
        sessions.value = await api.chats()
    } catch {
        return
    }
    if (sessions.value.some(session => session.id === sessionId.value && session.unreadCount > 0)) {
        await loadSession()
    }
}

async function loadSession(): Promise<void> {
    if (!sessionId.value) {
        detail.value = null
        return
    }
    const id = sessionId.value
    const opening = detail.value?.id !== id
    const listed = sessions.value.find(session => session.id === id)
    const read = ++reads
    try {
        const loaded = await api.chat(id)
        if (read < landed || id !== sessionId.value) {
            return
        }
        landed = read
        detail.value = loaded
        error.value = null
        if (opening) {
            restoreDraft(loaded.pendingFiles ?? [])
            void loadSetup(loaded)
        }
        markListedRead(detail.value.id)
        syncTrackedRun()
        await loadDoneRuns()
        if (opening) {
            await nextTick()
            land(listed)
        }
    } catch (caught) {
        if (read < landed || id !== sessionId.value) {
            return
        }
        // A later read of the thread on screen failing keeps what had loaded, the error above it, until the
        // next read lands and clears it; a chat that never loaded has nothing to keep, and another chat's
        // thread is not this one's. A chat the API no longer has is gone, not a blip: drop its thread.
        const gone = caught instanceof Error && caught.message === 'chat_not_found'
        if (!gone && detail.value?.id === id) {
            error.value = 'Could not refresh chat.'
            return
        }
        error.value = 'Chat not found.'
        detail.value = null
        syncTrackedRun()
    }
}

/**
 * #1732: the files you attached in a chat and never sent are still yours, waiting for your next message there, and
 * they hold places in its 5-files limit. Opening the chat puts them back in the composer, so the files that count are
 * always the ones shown, after a reload or a visit to another chat. Any attached while the chat was opening stay.
 */
function restoreDraft(waiting: ChatAttachedFile[]): void {
    pendingFiles.value = holding(waiting, true)
}

/** The files held for the next message with `added` among them, each file once; `first` puts them ahead of the rest. */
function holding(added: ChatAttachedFile[], first = false): ChatAttachedFile[] {
    const ids = new Set(added.map(file => file.id))
    const rest = pendingFiles.value.filter(file => !ids.has(file.id))
    return first ? [...added, ...rest] : [...rest, ...added]
}

/**
 * Built generically: any run linked to this session can be waiting, not only planning runs (#225).
 *
 * A gate the thread shows waiting that the list no longer has was decided, here or anywhere else (#1981): the thread
 * is read again before the list is taken, so its card turns to the outcome the read brings rather than leaving the
 * thread in between. True when it read the thread.
 *
 * One read is under way at a time. The 1 s poll does not wait for the last tick's, so a call made while one is
 * under way shares it rather than starting another thread read. `fresh` is for a decision just recorded: it waits
 * for the read under way, which may have taken the list before the decision, and then reads the list again.
 */
async function loadGates(fresh = false): Promise<boolean> {
    if (fresh && gatesLoading) {
        await gatesLoading
    }
    gatesLoading ??= readGates().finally(() => {
        gatesLoading = null
    })
    return gatesLoading
}

async function readGates(): Promise<boolean> {
    let waiting: GateRow[]
    try {
        waiting = await api.waitingGates()
    } catch {
        // Leave the thread's own decision-free view intact; the composer keeps working either way.
        return false
    }
    const still = new Set(waiting.map(gate => gate.id))
    const left = pendingGates.value.some(gate => !still.has(gate.id))
    if (left && !(await rereadThread())) {
        // The thread could not be read again: the list it was drawn with stays, so the card stays as it last was
        // rather than vanishing with nothing in its place, and the next poll tries again.
        return true
    }
    gates.value = waiting
    return left
}

/**
 * Reads the thread again, true once a read begun now or later has landed. Never throws: it runs from the poll, which
 * nothing awaits. `loadSession` keeps its own failures (the thread stays, with "Could not refresh chat." above it).
 */
async function rereadThread(): Promise<boolean> {
    const before = reads
    try {
        await loadSession()
    } catch {
        return false
    }
    return landed > before
}

/** Once a linked run succeeds, its `os.add_execution_link` resources become the thread's done card. */
async function loadDoneRuns(): Promise<void> {
    const succeededRunIds = (detail.value?.messages ?? [])
        .filter(row => row.role === 'tool' && row.toolName === 'os.start_task' && row.payload?.status === 'succeeded')
        .map(runId)
        .filter((id): id is string => id !== null && !doneRuns.value[id])
    await Promise.all(
        succeededRunIds.map(async id => {
            try {
                doneRuns.value = { ...doneRuns.value, [id]: await api.run(id) }
            } catch {
                // A transient failure to read the run's links shouldn't block the rest of the thread.
            }
        }),
    )
}

async function handleDecide(
    id: string,
    decision: GateDecision,
    note: string,
    answers?: CeoQuestionAnswer[],
    /** The payload as the CEO edited it before approving (os#911). Absent when they changed nothing. */
    edit?: Record<string, unknown>,
    /** The key of the choice the decision was made through, on a gate that offers choices. */
    choice?: string,
): Promise<void> {
    // #1796: a decision resumes the work it holds, which is model work, so without an AI account nothing is sent and
    // the prompt opens.
    if (!(await requireAiAccount())) return
    decidingGateId.value = id
    error.value = null
    try {
        // A question asked in this thread is answered as a reply to it (#229); a gate this session's own
        // run raised keeps going to the gate directly. Both land on the same `GatesService.decide`. Only an
        // `os.ask_ceo` question is ever asked in a thread, and a question has no editable fields, so an edit
        // only ever comes from a gate this session's own run raised — and goes with its decision, or Approve
        // would run the draft the CEO just reworded.
        // A gate that offers choices sends the one chosen with its decision, by whichever route the gate takes.
        if (choice && chatGateIds.value.has(id)) {
            await api.decideChatGate(sessionId.value, id, decision, note, undefined, choice)
        } else if (choice) {
            await api.decideGate(id, decision, note, undefined, undefined, choice)
        } else if (chatGateIds.value.has(id)) {
            await api.decideChatGate(sessionId.value, id, decision, note, answers)
        } else if (edit) {
            await api.decideGate(id, decision, note, answers, edit)
        } else if (answers) {
            await api.decideGate(id, decision, note, answers)
        } else {
            await api.decideGate(id, decision, note)
        }
        // The thread is read once either way: by `loadGates` when the gate has left the list, which turns its card
        // to the outcome (#1981), and here otherwise, for what the decision wrote back into it. The list is read after
        // the decision, not taken from a poll's read already under way.
        if (!(await loadGates(true))) {
            await loadSession()
        }
    } catch (caught) {
        // Said the way a refused tool call is (#1256): the cause the API named, or plainly not recorded.
        const refused = decisionRefusal(caught, threadMember.value?.name)
        if (refused.reread && !(await loadGates(true))) {
            await loadSession()
        }
        error.value = refused.message
    } finally {
        decidingGateId.value = null
    }
}

/**
 * A decision shows on the call's card at once, which fades and closes up its space without waiting on the API,
 * as a decided gate does; a card showing its decision takes no other. The reply the resumed turn wrote is drawn
 * as soon as the API answers, under the card while it goes. If the API refuses, the card comes back as it was.
 * The card leaves the page once both the hold has run and the API has answered, so a call the API is still running
 * after the hold stays a closed card (see `onCard`), and a refusal that lands then still brings it back.
 */
async function decideCall(row: ChatMessageRow, decision: ChatToolCallDecision): Promise<void> {
    if (!detail.value || !canDecide(row)) {
        return
    }
    // #1796: the member's turn carries on from the decision, whichever it is: model work, so it asks the guard first.
    if (!(await requireAiAccount())) return
    const chatId = detail.value.id
    const memberName = memberLabel(detail.value.member)
    error.value = null
    const record = (callId: string) => api.decideChatToolCall(chatId, callId, decision)
    decidingChatId.value = chatId
    const outcome = await decideToolCall(row, decision, record, async decided => {
        // Answered once the turn is done: if another chat was opened meanwhile, this one is drawn when it is reopened.
        if (chatId !== sessionId.value) {
            await loadDirectory()
            return
        }
        landed = ++reads
        detail.value = decided
        syncTrackedRun()
        if (grantsOpen.value) {
            await loadGrants()
        }
        await loadDirectory()
        await nextTick()
        scrollToEnd()
    })
    if (outcome && !outcome.recorded && chatId === sessionId.value) {
        const refused = decisionRefusal(outcome.refusal, memberName)
        // A call no longer waiting has an outcome the thread already holds: read it, so the card that just came
        // back gives way to what happened. The message goes up after the read, which clears the last one.
        if (refused.reread) {
            await loadSession()
        }
        error.value = refused.message
    }
}

/** The waiting message whose withdrawal is on its way (#284), so a second click does not send another. */
const withdrawingId = ref<string | null>(null)
/**
 * Why the last withdrawal in a chat was refused (#284), for that chat only. Kept apart from `error`, which every read
 * of the thread clears: the read that follows a refusal is what draws the message as sent, and must not take the
 * reason away with it. It goes when the next withdrawal starts, when you send, and when you open another chat.
 */
const withdrawNotice = ref<{ chatId: string; text: string } | null>(null)

/**
 * Takes a waiting message back before the member reads it (#284). The thread the API answers with is drawn without
 * it. If the API refuses (the member picked it up first, most often), the person is told why in plain words and
 * the thread is read again, so the message is drawn as it now stands: sent, with no waiting mark and no Withdraw.
 */
async function withdraw(messageId: string): Promise<void> {
    // The control is shut while one is on its way, so only a thread on screen and no withdrawal under way reach here.
    if (!detail.value || withdrawingId.value) return
    const chatId = detail.value.id
    const memberName = memberLabel(detail.value.member)
    withdrawingId.value = messageId
    withdrawNotice.value = null
    try {
        const withdrawn = await api.withdrawChatMessage(chatId, messageId)
        if (chatId === sessionId.value) {
            landed = ++reads
            detail.value = withdrawn
        }
    } catch (caught) {
        withdrawNotice.value = { chatId, text: withdrawRefusal(caught, memberName) }
        await loadSession()
    } finally {
        withdrawingId.value = null
    }
}

/** What a refused withdrawal says (#284): never the API's code, which a person cannot act on. */
function withdrawRefusal(caught: unknown, memberName: string): string {
    if (caught instanceof Error && caught.message === 'chat_message_picked_up') {
        return `${memberName} had already read that message, so it was not withdrawn. It is shown as sent.`
    }
    return 'Could not withdraw that message. The thread shows where it stands.'
}

async function loadGrants(): Promise<void> {
    if (!detail.value) {
        return
    }
    try {
        grants.value = await api.chatToolGrants(detail.value.memberId)
    } catch {
        error.value = 'Could not load allowed tools.'
    }
}

async function toggleGrants(): Promise<void> {
    grantsOpen.value = !grantsOpen.value
    if (grantsOpen.value) {
        await loadGrants()
    }
}

async function revokeGrant(grant: ChatToolGrantRow): Promise<void> {
    revokingGrantId.value = grant.id
    error.value = null
    try {
        await api.revokeChatToolGrant(grant.id)
        grants.value = grants.value.filter(row => row.id !== grant.id)
    } catch {
        error.value = 'Could not revoke that.'
    } finally {
        revokingGrantId.value = null
    }
}

async function openMember(member: Member): Promise<void> {
    const existing = latestFor(member.id)
    if (existing) {
        await router.push(`/chat/${existing.id}`)
        return
    }
    await startChat(member.id)
}

async function startChat(memberId: string): Promise<void> {
    starting.value = memberId
    error.value = null
    try {
        const session = await api.createChat(memberId)
        await loadDirectory()
        await router.push(`/chat/${session.id}`)
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not start chat.'
    } finally {
        starting.value = null
    }
}

/**
 * Sends what you wrote. The member being at work does not stop it (#1356): a message sent while it replies, or
 * while another of yours is on its way, is kept by the API and read before the member's next step, so it is drawn
 * at once marked as waiting. Only a closed thread, or a tool call's decision on its way, holds it back.
 *
 * An AI provider key is never sent (#1716): it is taken out of the composer, never drawn in the thread, and the
 * composer points at the AI account card instead. The API refuses one all the same, which is the guarantee; this
 * keeps the key off the wire and tells you where it goes.
 */
async function send(): Promise<void> {
    if (!detail.value || !canSend.value || setupSaving.value) {
        return
    }
    error.value = null
    // #284: the reason a withdrawal was refused explains that withdrawal only, not the conversation after it.
    withdrawNotice.value = null
    keyInChat.value = false
    if (looksLikeProviderKey(draft.value)) {
        draft.value = ''
        keyInChat.value = true
        return
    }
    // #1796: a message starts the member's turn, which is model work. Without an AI account nothing is sent, what was
    // typed stays in the composer, and the prompt opens.
    if (!(await requireAiAccount())) return
    const id = detail.value.id
    const content = draft.value
    // #1742: a typed role is matched to the closest known role and confirmed before anything is saved or sent. The
    // files attached with it wait with it, out of the composer, to go with the answer that confirms it.
    if (askToConfirmRole(content)) {
        roleGuessFiles.value = { chatId: id, files: [...takeRoleGuessFiles(), ...pendingFiles.value] }
        draft.value = ''
        pendingFiles.value = []
        attachRefusal.value = null
        return
    }
    // #1738: in the setup conversation, text typed while the kind of work or the role is asked answers it, saved
    // through the setup API before it is sent. A refusal keeps it in the composer, with the API's reason above it.
    // Files sent with no words (#2085) answer nothing.
    if (!(await saveSetupAnswer(content.trim() ? typedSetupAnswer(content) : null))) {
        return
    }
    // #1732: the files attached for this message go with it, and the composer starts the next one with none.
    const carried = pendingFiles.value
    draft.value = ''
    pendingFiles.value = []
    attachRefusal.value = null
    await deliver(id, content, carried, true)
}

/**
 * Sends `content` in the thread, drawn at once at its end. `fromComposer` is what was typed: a failed send puts it
 * back there. A setup chip's label is not, so a failed chip never overwrites what is being typed.
 */
async function deliver(id: string, content: string, carried: ChatAttachedFile[], fromComposer: boolean): Promise<void> {
    sends += 1
    const sent: Outgoing = {
        // `pending` when it is the only one on its way, as it was before you could send another; numbered beside it.
        key: outboxHere.value.length === 0 ? 'pending' : `pending-${sends}`,
        chatId: id,
        text: content.trim(),
        after: yourMessageCount() + unlanded().length,
        waiting: replying.value,
    }
    outbox.value = [...outbox.value, sent]
    await nextTick()
    scrollToEnd()
    try {
        const replied = await post(id, content, carried)
        // The reply comes back once it is done, by when another chat may be open: it is drawn only in its own.
        const here = id === sessionId.value
        if (here) {
            landed = ++reads
            detail.value = replied
            syncTrackedRun()
        }
        await loadDirectory()
        if (here) {
            await nextTick()
            scrollToEnd()
        }
    } catch (caught) {
        await notSent(caught, id, sent, fromComposer ? content : null, carried)
    } finally {
        outbox.value = outbox.value.filter(other => other.key !== sent.key || other.chatId !== sent.chatId)
    }
}

/**
 * A send that failed: unless the API kept it after all, what was typed (`typed`, null for a setup chip's label) and
 * the files it carried go back in the composer, with the reason above it.
 */
async function notSent(
    caught: unknown,
    id: string,
    sent: Outgoing,
    typed: string | null,
    carried: ChatAttachedFile[],
): Promise<void> {
    // The API took it for a key (#1716): it kept nothing, and a key is not put back on screen.
    if (isProviderKeyRefusal(caught)) {
        // Refused before its files were bound to it: they are still waiting for a message, so they are kept,
        // in this chat's composer only. Another chat opened meanwhile gets them back when this one is reopened.
        if (id === sessionId.value) {
            keyInChat.value = true
            pendingFiles.value = holding(carried, true)
        }
        return
    }
    // Putting back a message the API kept would only invite sending it twice: the thread carries on from the
    // read, and the reply arrives by the poll.
    if (await sentAnyway(id, sent.text, sent.after)) {
        return
    }
    // A chat left meanwhile gets its words back for when it is reopened, its files from its own read then, and says
    // nothing in the one open now.
    if (id !== sessionId.value) {
        if (typed !== null) {
            drafts.set(id, typed)
        }
        return
    }
    if (typed !== null) {
        draft.value = typed
    }
    pendingFiles.value = holding(carried, true)
    error.value = sendRefusal(caught)
}

/** One message to the API, with the files attached for it (#1732) when there are any. */
function post(id: string, content: string, carried: ChatAttachedFile[]): Promise<ChatDetail> {
    return carried.length > 0
        ? api.postChatMessage(
              id,
              content,
              carried.map(file => file.id),
          )
        : api.postChatMessage(id, content)
}

/**
 * What text typed in the setup conversation saves (#1738): the answer to the kind of work or the role while that
 * question is open, by the shared rules (a chip's label saves that chip). Null anywhere else: the text is then only
 * a message.
 */
function typedSetupAnswer(content: string): Partial<WorkProfile> | null {
    return setupAnswerFromTyped(setupOpen.value, content, offeredKinds(detail.value?.messages ?? []))
}

/**
 * Saves a setup answer through the setup API (#1738) before what was said is sent, so the COO's next question reads
 * it saved. The server refuses an answer that does not hold the fixed structure, and then nothing is sent and its
 * own sentence is shown. True when it saved, or when there was nothing to save.
 *
 * When the refusal is the server asking the kind of work first (`isWorkFirstRefusal`), the person's own profile has
 * none even though the step does: the member who began setup left part-way. The chat then asks the kind of work, so
 * the person's answer to it can replace the step (review of #1953).
 */
async function saveSetupAnswer(answers: Partial<WorkProfile> | null): Promise<boolean> {
    const chat = detail.value
    if (!answers || !chat) {
        return true
    }
    setupSaving.value = true
    try {
        takeSetupState(chat.id, await api.saveOnboardingStep('work_profile', { ...answers }, 'chat', chat.id))
        return true
    } catch (caught) {
        if (isWorkFirstRefusal(caught) && chat.id === sessionId.value) {
            setupSaved.value = {}
        }
        error.value = caught instanceof Error ? caught.message : 'Could not save that answer.'
        return false
    } finally {
        setupSaving.value = false
    }
}

/** Whether a setup chip or its typed answer may be taken now: the thread is open and nothing else is on its way. */
function mayAnswerSetup(): boolean {
    return !!detail.value && detail.value.status !== 'closed' && !decidingHere.value && !setupSaving.value
}

/**
 * A setup chip (#1738): its answer saved, then its label sent as what the person said, so the conversation shows
 * the choice and the COO asks the next question.
 */
async function chooseSetupChip(value: string): Promise<void> {
    const question = setupOpen.value
    const chip = setupReplyChips.value?.replies.find(reply => reply.value === value)
    if (!question || !chip || !mayAnswerSetup()) {
        return
    }
    const id = detail.value?.id ?? ''
    error.value = null
    // #1796: the chip's label is sent as a message, which starts the COO's turn: nothing is saved or sent without one.
    if (!(await requireAiAccount())) return
    const guess = question === 'role' ? roleGuess.value : null
    if (guess) {
        await confirmRole(id, guess, value, chip.label)
        return
    }
    if (await saveSetupAnswer(setupAnswerFromChip(question, value))) {
        await deliver(id, chip.label, [], false)
    }
}

/**
 * A team chip (#2216): its catalog team saved as the proposal, then its name sent as what the person said, so the
 * conversation shows the choice and the COO's turn runs with setup at the names. "Tell your COO what you need" saves
 * nothing: it sends the sentence, and the COO takes it from there.
 */
async function chooseSetupTeam(value: string): Promise<void> {
    const chat = detail.value
    const chip = setupProposalReplies.value.find(reply => reply.value === value)
    if (!chat || !chip || !mayAnswerSetup()) {
        return
    }
    error.value = null
    // #1796: the chip's label is sent as a message, which starts the COO's turn: nothing is saved or sent without one.
    if (!(await requireAiAccount())) return
    if (value !== TELL_YOUR_COO_VALUE) {
        setupSaving.value = true
        try {
            takeSetupState(
                chat.id,
                await api.saveOnboardingStep('team_proposal', { catalogTeamId: value }, 'chat', chat.id),
            )
        } catch (caught) {
            error.value = caught instanceof Error ? caught.message : 'Could not save that choice.'
            return
        } finally {
            setupSaving.value = false
        }
    }
    await deliver(chat.id, chip.label, [], false)
}

/**
 * The names form (#2216): the names saved, or `{}` for Skip, which keeps the template's name and the role titles. The
 * server starts the assembly on that save, so the state taken in shows its log in place of the form. Then the team's
 * name, or that the titles were kept, is sent as the person's message. A refusal (the name is taken, no room for the
 * teammates…) is the server's plain words, shown on the form, which keeps what was typed to change and save again.
 */
async function saveSetupNames(answers: NamesAnswers | null): Promise<void> {
    const chat = detail.value
    const template = setupNamesTemplate.value
    if (!chat || !template || !mayAnswerSetup()) {
        return
    }
    error.value = null
    setupNamesFailure.value = null
    if (!(await requireAiAccount())) return
    setupSaving.value = true
    try {
        takeSetupState(chat.id, await api.saveOnboardingStep('names', answers ? { ...answers } : {}, 'chat', chat.id))
    } catch (caught) {
        if (chat.id === sessionId.value) {
            setupNamesFailure.value = caught instanceof Error ? caught.message : 'Could not save those names.'
        }
        return
    } finally {
        setupSaving.value = false
    }
    await deliver(chat.id, namesSaidAs(answers, template), [], false)
}

/**
 * The role question's typed answer, when it names no role's chip (#1742, O13): matched to the closest known role and
 * put to the person to confirm, saving nothing. True when it was taken that way.
 */
function askToConfirmRole(typed: string): boolean {
    const guess = setupOpen.value === 'role' ? roleGuessFor(typed) : null
    if (!guess) {
        return false
    }
    roleGuess.value = guess
    void nextTick(scrollToEnd)
    return true
}

/**
 * A guessed role, confirmed (#1742): accepting it saves the matched role with the person's own words, then sends what
 * they typed; picking another role saves that role alone, then sends its label. Either way the files attached with
 * the typed role go with it. A refused save keeps the guess, and its files, on the question it was asked on; when the
 * server asks the kind of work first instead, what was typed and its files go back in the composer, so none is lost.
 */
async function confirmRole(id: string, guess: RoleGuess, value: string, label: string): Promise<void> {
    const accepted = value === guess.matched
    const role = accepted ? { role: guess.matched, text: guess.kept } : { role: value }
    // Taken off the guess while the answer saves, so the question moving on does not also put them back.
    const carried = takeRoleGuessFiles()
    if (await saveSetupAnswer({ role } as Partial<WorkProfile>)) {
        roleGuess.value = null
        await deliver(id, accepted ? guess.typed : label, carried, false)
        return
    }
    if (roleGuess.value === guess && setupOpen.value === 'role') {
        roleGuessFiles.value = carried.length > 0 && id === sessionId.value ? { chatId: id, files: carried } : null
    } else {
        returnToComposer(id, carried)
    }
    if (setupOpen.value !== 'role' && !draft.value.trim()) {
        draft.value = guess.typed
    }
}

/** The files held with the typed role in the chat on screen (#1742), taken off it: none for another chat's. */
function takeRoleGuessFiles(): ChatAttachedFile[] {
    const held = roleGuessFiles.value
    roleGuessFiles.value = null
    return held && held.chatId === sessionId.value ? held.files : []
}

/** Files taken off a typed role no longer asked about, back in the composer of `chatId` when it is the chat on screen. */
function returnToComposer(chatId: string, files: ChatAttachedFile[]): void {
    if (files.length > 0 && chatId === sessionId.value) {
        pendingFiles.value = holding(files, true)
    }
}

/**
 * “Prefer step-by-step?” (#1742): moves setup to the guided path from this conversation, then opens the guided
 * screens at the step the server says is current. The conversation goes with it, so the guided screens can hand back
 * to this same conversation, its kinds of work included.
 *
 * The switch names the tab's session (`onboardingSession`), the one the guided screens read and save with, not this
 * conversation's: the server takes a read from any session but the last writer's as somebody coming back, so
 * switching under the conversation's id would log a false `setup_resumed` on the guided screen's first read.
 */
async function preferStepByStep(): Promise<void> {
    const chat = detail.value
    if (!chat || setupMoving.value) {
        return
    }
    setupMoving.value = true
    error.value = null
    try {
        await api.switchOnboardingPath('guided', onboardingSession())
        await router.push({ path: GUIDED_SETUP_PATH, query: { chat: chat.id } })
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not open the step-by-step screens.'
    } finally {
        setupMoving.value = false
    }
}

/**
 * “Advanced mode: skip setup and take me to the dashboard” (#1742, O12): setup is skipped on the server with every
 * answer kept, the portal's Advanced mode is turned on, and the person lands on the dashboard.
 */
async function skipSetup(): Promise<void> {
    const chat = detail.value
    if (!chat || setupMoving.value) {
        return
    }
    setupMoving.value = true
    error.value = null
    try {
        setupState.value = await api.skipOnboarding('chat', chat.id)
        setMode('advanced')
        await router.push('/')
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not skip setup.'
    } finally {
        setupMoving.value = false
    }
}

/**
 * Text typed under a setup chip's "Something else" (#1738): saved as the open question's typed answer, then sent.
 * A refused save sends nothing and leaves the text in its input (`setupDraft`). An AI provider key is never saved
 * or sent (#1716), and is taken out of the input, as in the composer.
 */
async function answerSetupTyped(text: string): Promise<void> {
    if (!mayAnswerSetup()) {
        return
    }
    error.value = null
    keyInChat.value = false
    if (looksLikeProviderKey(text)) {
        setupDraft.value = ''
        keyInChat.value = true
        return
    }
    // #1796: the answer is sent as a message, which starts the COO's turn: nothing is saved or sent without one.
    if (!(await requireAiAccount())) return
    if (askToConfirmRole(text)) {
        setupDraft.value = ''
        return
    }
    const id = detail.value?.id ?? ''
    if (await saveSetupAnswer(typedSetupAnswer(text))) {
        await deliver(id, text.trim(), [], false)
    }
}

/**
 * The one way a file is attached to the message being composed (#1732): the attach button, a drop on the
 * conversation and a paste into the composer all end here. The API stores the files and they are held as pending
 * until the message is sent with them; a refusal (a kind chat does not take, or over a limit) is shown in the API's
 * own words beside the composer, attaching nothing and leaving what was typed as it was.
 *
 * Only to the chat you are in: not to the one before it while the one you opened is still loading, and an answer
 * that lands after you have opened another chat is not shown there. Files stored by then wait in the draft of the
 * chat they were attached in, and the composer restores them when you open it again (`restoreDraft`).
 *
 * Files over the size or count limits are refused here before any is read or uploaded (`refusedBeforeUpload`), so
 * the refusal never depends on the API answering a body too large to read.
 */
async function attach(files: File[]): Promise<void> {
    const id = detail.value?.id
    if (!id || id !== sessionId.value || files.length === 0) {
        return
    }
    // A file still uploading, or failed and waiting for Retry, holds its place as a stored one does (#2085).
    const refused = refusedBeforeUpload(files, pendingFiles.value.length + uploads.value.length)
    if (refused) {
        attachRefusal.value = refused
        return
    }
    attachRefusal.value = null
    // #2085: each file shows its chip at once and uploads on its own request, so one failure leaves the rest attached.
    const started = files.map((file): Upload => ({ key: ++uploadKeys, chatId: id, file, state: 'uploading' }))
    uploads.value = [...uploads.value, ...started]
    await Promise.all(started.map(item => upload(item)))
}

/**
 * One file's upload (#2085). Landed, it becomes an ordinary unsent chip; taken off the message while on its way, the
 * stored file is removed again at once, so it never shows as held. A failure of the network or the server leaves a
 * failed chip with Retry; the API refusing the file itself (a kind chat does not take, a limit) keeps today's refused
 * line in its own words and attaches nothing, since retrying cannot help. Only in the chat it was attached in: one
 * that lands while another chat is open waits in its own chat's draft (`restoreDraft`), and one that lands after its
 * chat was left and opened again is held there.
 */
async function upload(item: Upload): Promise<void> {
    try {
        const stored = await api.attachChatFiles(item.chatId, [item.file])
        if (cancelledUploads.delete(item.key)) {
            await Promise.all(stored.map(file => api.removeChatFile(item.chatId, file.id).catch(() => undefined)))
            return
        }
        if (!uploading(item.key)) {
            // Left behind by opening another chat (`watch(sessionId)` clears `uploads`). If its own chat is open
            // again, the file is held there, as before #2085; the reopen's read may already list it (`holding`).
            if (item.chatId === sessionId.value) {
                pendingFiles.value = holding(stored)
            }
            return
        }
        dropUpload(item.key)
        pendingFiles.value = holding(stored)
    } catch (caught) {
        if (cancelledUploads.delete(item.key) || !uploading(item.key)) {
            return
        }
        if (uploadFailed(caught)) {
            uploads.value = uploads.value.map(other => (other.key === item.key ? { ...other, state: 'failed' } : other))
            return
        }
        dropUpload(item.key)
        attachRefusal.value = caught instanceof Error ? caught.message : 'Could not attach that file.'
    }
}

/** Whether an upload is still on this chat's composer: not landed, removed, or left behind by opening another chat. */
function uploading(key: number): boolean {
    return uploads.value.some(item => item.key === key)
}

function dropUpload(key: number): void {
    uploads.value = uploads.value.filter(item => item.key !== key)
}

/**
 * Whether an attach failed on the way rather than being refused (#2085): the network (`fetch` throws a TypeError) or
 * the server (5xx). Only then can Retry help.
 */
function uploadFailed(caught: unknown): boolean {
    if (caught instanceof TypeError) {
        return true
    }
    const failure = requestFailure(caught)
    return failure !== null && failure.status >= 500
}

/** Retry on a failed chip (#2085): uploads that one file again. */
async function retryUpload(key: number): Promise<void> {
    const item = uploads.value.find(other => other.key === key)
    if (item?.state !== 'failed') {
        return
    }
    uploads.value = uploads.value.map(other => (other.key === key ? { ...other, state: 'uploading' } : other))
    await upload(item)
}

/**
 * The × on a chip not stored yet (#2085). A failed one is simply dropped; one still uploading is dropped and marked
 * cancelled, so if its upload lands anyway the stored file is removed again (`upload`).
 */
function removeUpload(item: Upload): void {
    if (item.state === 'uploading') {
        cancelledUploads.add(item.key)
    }
    dropUpload(item.key)
}

/**
 * The × on a stored chip (#2085): the API deletes the unsent file, so it frees its place in the limits and can never
 * be sent, and the chip goes. A removal that fails keeps the chip and says so in plain words.
 */
async function removeHeld(file: ChatAttachedFile): Promise<void> {
    const id = detail.value?.id
    if (!id) {
        return
    }
    try {
        await api.removeChatFile(id, file.id)
        if (id === sessionId.value) {
            pendingFiles.value = pendingFiles.value.filter(other => other.id !== file.id)
            attachRefusal.value = null
        }
    } catch {
        if (id === sessionId.value) {
            attachRefusal.value = `Could not remove ${file.filename}. Try again.`
        }
    }
}

/**
 * #2085: the plain notice on an image's chip in a chat whose teammate is not shown images, as it is attached rather
 * than after it is sent. None for any other file, or where the teammate is shown images or the API does not say.
 */
function imageNotice(kind: string | null): string | undefined {
    const chat = detail.value
    if (chat?.teammateSeesImages !== false || (kind !== 'png' && kind !== 'jpg')) {
        return undefined
    }
    return `${chat.member.name} can't see images, so they won't be able to read this file.`
}

/**
 * The one notice line of a file held for the next message: that the teammate can't see it (an image, #2085), else
 * what reading it will cost (#1736), the API's own sentence for a large file, shown before Send and never blocking
 * it. A file still uploading or whose upload failed is drawn by its upload chip instead, whose state outranks both.
 */
function heldNotice(file: ChatAttachedFile): string | undefined {
    return imageNotice(file.type) ?? file.costNote ?? undefined
}

/** The attach button: opens the browser's file picker, whose choice `onChosen` attaches. */
function chooseFiles(): void {
    attachInput.value?.click()
}

async function onChosen(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files ?? [])
    // Cleared, so choosing the same file again is a change the input reports.
    input.value = ''
    await attach(files)
}

/** A paste carrying files attaches them, a nameless clipboard image under a generated name; text pastes as text. */
async function onPaste(event: ClipboardEvent): Promise<void> {
    const files = transferFiles(event.clipboardData)
    if (files.length === 0) {
        return
    }
    event.preventDefault()
    await attach(files.map(file => namedForAttaching(file)))
}

/** A file dragged into the conversation shows the drop state; a drag of text or a link does not. */
function onDragEnter(event: DragEvent): void {
    if (!isFileDrag(event.dataTransfer)) {
        return
    }
    event.preventDefault()
    dragDepth += 1
    dropping.value = true
}

/** Takes a file drag over the pane, so the browser lets it drop here rather than open the file. */
function onDragOver(event: DragEvent): void {
    if (isFileDrag(event.dataTransfer)) {
        event.preventDefault()
    }
}

function onDragLeave(event: DragEvent): void {
    if (!isFileDrag(event.dataTransfer)) {
        return
    }
    dragDepth = Math.max(0, dragDepth - 1)
    dropping.value = dragDepth > 0
}

/** Files dropped anywhere on the conversation attach exactly as the button's do. */
async function onDrop(event: DragEvent): Promise<void> {
    dragDepth = 0
    dropping.value = false
    const files = transferFiles(event.dataTransfer)
    if (files.length === 0) {
        return
    }
    event.preventDefault()
    await attach(files)
}

/**
 * Why a send failed, in words. An API from before #1356 refuses a message sent mid-reply with `chat_replying`:
 * that code is never shown, since the person only has to send it again.
 */
function sendRefusal(caught: unknown): string {
    if (!(caught instanceof Error)) {
        return 'Could not send.'
    }
    return caught.message === 'chat_replying'
        ? 'The member was busy and could not take that message. Send it again.'
        : caught.message
}

function preview(session: ChatSessionRow | undefined): string {
    const text = session?.lastMessage?.content?.trim()
    return text ? text.slice(0, 80) : 'New chat'
}

/** The replies you have not read in a thread, except the one on screen: you are reading that one. */
function unreadIn(session: ChatSessionRow): number {
    return session.id === sessionId.value ? 0 : session.unreadCount
}

/** Opening a thread read it, so its row says so now rather than on the list's next refresh. */
function markListedRead(id: string): void {
    sessions.value = sessions.value.map(session =>
        session.id === id && session.unreadCount > 0 ? { ...session, unread: false, unreadCount: 0 } : session,
    )
}

async function openSession(id: string): Promise<void> {
    historyOpen.value = false
    await router.push(`/chat/${id}`)
}

/**
 * Copies the thread's canonical link, `/chat/:id`, to pass to a colleague: with Chat access in this enterprise they
 * open it as a guest, and it stays out of their list.
 */
async function copyLink(): Promise<void> {
    if (!detail.value) {
        return
    }
    try {
        await navigator.clipboard.writeText(`${window.location.origin}/chat/${detail.value.id}`)
    } catch {
        error.value = 'Could not copy the link.'
        return
    }
    linkCopied.value = true
    if (linkCopiedTimer) {
        clearTimeout(linkCopiedTimer)
    }
    linkCopiedTimer = setTimeout(() => {
        linkCopied.value = false
    }, 2000)
}

const runId = toolRunId

/** The run this thread is tracking live, when it is the one `row` started; null for any other row. */
function trackedRunOf(row: ChatMessageRow): RunDetail | null {
    const id = runId(row)
    return id && id === trackedRunId.value ? trackedRun.value : null
}

/**
 * A linked run's status word: live for the run being tracked, else the run's current status as the chat
 * read serves it (#1761), and only for a row read from an older API the status the row was created with.
 */
function runStatus(row: ChatMessageRow): string {
    return trackedRunOf(row)?.run.status ?? row.run?.status ?? String(row.payload?.status ?? '')
}

/**
 * The status line under a linked-run tool message: the run's own live status once this message is
 * the one being tracked (with its failed/blocked reason spelled out), falling back to the status the
 * chat read serves for anything not currently tracked (`runStatus`).
 */
function liveRunText(row: ChatMessageRow): string {
    const tracked = trackedRunOf(row)
    if (tracked) {
        const run = tracked.run
        if (run.status === 'failed') {
            const reason = failReason(run, tracked.events)
            return reason ? errorReason(reason, uiMode.value) : run.status
        }
        if (run.status === 'blocked') {
            return blockedReason(run) ?? run.status
        }
        // Succeeded, but its task has no next step for the outcome it ended on: the pipeline stopped there.
        const stopped = runStoppedLine(run)
        if (stopped) {
            return stopped
        }
    }
    return runStatus(row)
}

/** What a completed, linked run built — `os.add_execution_link` resources rolled up onto its execution. */
function doneResources(row: ChatMessageRow): ExecutionResource[] {
    const id = runId(row)
    if (!id) {
        return []
    }
    return doneRuns.value[id]?.execution?.resources ?? []
}

/**
 * The tool rows behind one reply, as the log shows them, with a linked run's status kept live and its card
 * named for its task (#1761).
 */
function toolItems(tools: ChatMessageRow[]): ChatToolItem[] {
    return tools.map(row => {
        const id = runId(row)
        const slug = typeof row.payload?.slug === 'string' ? row.payload.slug : null
        return {
            id: row.id,
            tool: row.toolName ? shownTool(row.toolName) : 'Tool update',
            runId: id,
            slug,
            status: id ? liveRunText(row) : null,
            result: id ? runStatus(row) : null,
            taskLabel: id ? runTaskLabel(row.run, slug, uiMode.value) : null,
            resources: id ? doneResources(row) : [],
            payload: toolPayload(row),
        }
    })
}

/** The tool log under one entry of the thread, as ChatConversation draws it: empty for an entry with no tool rows. */
function toolLogOf(entryId: string): ChatToolItem[] {
    return toolItems(toolsByKey.value.get(entryId) ?? [])
}

/**
 * What the thread ends with: its messages, the decisions waiting on you, and the run in progress. When any
 * of it changes while you are at the end, the thread stays at the end; read further up, and it leaves you be.
 * Measured before the change is drawn, scrolled after.
 */
watch(
    () => [
        entries.value.length,
        pendingGates.value.map(pending => pending.id).join(),
        indicatorActive.value,
        replying.value,
    ],
    async () => {
        if (!atEnd()) {
            return
        }
        await nextTick()
        scrollToEnd()
    },
    { flush: 'pre' },
)
</script>

<template>
	<!--
		Mobile-first, on the md boundary the shared sidebar, input and dialog primitives key off: one column below
		it (the thread, or the member list on /chat), the list beside the thread from md up. The shell takes the
		height App.vue holds to the dynamic viewport and never scrolls itself; only the thread's log does.
	-->
	<section class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex-row">
		<aside
			class="w-full shrink-0 border-r md:flex md:w-72 md:flex-none md:flex-col"
			:class="sessionId ? 'hidden md:flex' : 'flex min-h-0 flex-1 flex-col'"
		>
			<div class="border-b p-4">
				<ZhPageTitle>Chat</ZhPageTitle>
				<p class="text-muted-foreground text-sm">{{ CHAT_INTRO }}</p>
			</div>
			<p v-if="error && !sessionId" class="text-destructive px-4 py-2 text-sm">{{ error }}</p>
			<ZhChatList
				class="min-h-0 flex-1 overflow-y-auto p-2"
				:items="people"
				label="Team members"
				selectable
				@select="selectPerson"
			>
				<template #avatar="{ item }">
					<MemberAvatar :member="{ name: item.member.name, avatarUrl: item.member.avatarUrl ?? null }" class="size-full" />
				</template>
			</ZhChatList>
		</aside>

		<!--
			The thread column shrinks and clips, so nothing in a message can widen the page. It is the drop target for
			files too (`onDrop`), the whole conversation rather than the message box, with a visible state while a
			file is dragged over it.
		-->
		<div
			class="min-h-0 min-w-0 flex-1 flex-col"
			:class="[sessionId ? 'flex' : 'hidden md:flex', dropping ? 'bg-primary/5 ring-primary ring-2 ring-inset' : '']"
			data-testid="chat-drop-target"
			:data-dropping="dropping ? 'true' : undefined"
			@dragenter="onDragEnter"
			@dragover="onDragOver"
			@dragleave="onDragLeave"
			@drop="onDrop"
		>
			<!-- The conversation Create task draws too (issue 244): one component, so the two pages cannot drift. -->
			<ChatConversation
				v-if="detail"
				ref="thread"
				class="chat-thread"
				:member="threadMember ?? detail.member"
				:role="roleOf(detail.memberId)"
				:entries="entries"
				:thinking="replying"
				:details-open="uiMode === 'advanced'"
				:tool-log="toolLogOf"
				:calls="waitingCalls"
				:call-decided="callDecided"
				:deciding="decidingHere"
				v-model:draft="draft"
				:can-send="canSend"
				:closed="detail.status === 'closed'"
				@decide="decideCall"
				@send="send"
				@paste="onPaste"
			>
				<template #avatar><MemberAvatar :member="threadMember ?? detail.member" class="size-full" /></template>
				<!-- An outcome entry (os 283), `<task name> · <outcome>`, and its run, as the run line in ChatToolLog reads. -->
				<template #system-entry="{ entry }">
					<ZhSystemLabel as="span" tone="muted" data-testid="chat-outcome">{{ entry.text }}</ZhSystemLabel>
					<RouterLink v-if="entry.id && outcomeRuns.get(entry.id)" :to="`/runs/${outcomeRuns.get(entry.id)}`">
						<ZhSystemLabel as="span" tone="system">Open run</ZhSystemLabel>
					</RouterLink>
				</template>
				<template #sender-avatar="{ sender }">
					<MemberAvatar :member="{ name: sender.name, avatarUrl: sender.avatarUrl ?? null }" class="size-full" />
				</template>
				<template #actions>
					<!-- The setup conversation offers the guided screens while setup is open (os 1742). -->
					<Button
						v-if="offerStepByStep"
						type="button"
						size="xs"
						variant="link"
						data-testid="chat-prefer-step-by-step"
						:disabled="setupMoving"
						@click="preferStepByStep"
					>
						{{ PREFER_STEP_BY_STEP }}
					</Button>
					<Button
						type="button"
						size="xs"
						variant="outline"
						class="md:hidden"
						data-testid="chat-list-trigger"
						aria-haspopup="dialog"
						:aria-expanded="listOpen"
						@click="listOpen = true"
					>
						Members
					</Button>
					<Button
						type="button"
						size="xs"
						variant="outline"
						data-testid="chat-history-toggle"
						:aria-expanded="historyOpen"
						@click="historyOpen = !historyOpen"
					>
						History
					</Button>
					<Button
						type="button"
						size="xs"
						variant="outline"
						data-testid="chat-tool-grants-toggle"
						:aria-expanded="grantsOpen"
						@click="toggleGrants"
					>
						Allowed tools
					</Button>
					<Button
						type="button"
						size="xs"
						variant="outline"
						data-testid="chat-copy-link"
						aria-label="Copy link"
						title="Copy this chat’s link to share with a colleague"
						@click="copyLink"
					>
						{{ linkCopied ? 'Link copied' : 'Copy link' }}
					</Button>
					<Button
						type="button"
						size="xs"
						variant="outline"
						data-testid="chat-new"
						aria-label="New chat"
						title="New chat"
						:disabled="starting === detail.memberId"
						@click="startChat(detail.memberId)"
					>
						+
					</Button>
				</template>
				<template #top>
					<p v-if="guest" data-testid="chat-guest" class="bg-muted/20 text-muted-foreground border-b px-4 py-2 text-xs">
						You’re a guest in somebody else’s chat, here by its link. It stays out of your list and History,
						and only its owner can close it.
					</p>
					<section v-if="historyOpen" data-testid="chat-history" class="border-b text-sm">
						<header class="bg-muted/20 flex items-center justify-between gap-3 border-b px-4 py-3">
							<ZhSectionHeading>
								<template #default>Chats with {{ memberLabel(detail.member) }}</template>
								<template v-if="memberHistory.length" #count>{{ memberHistory.length }}</template>
							</ZhSectionHeading>
						</header>
						<ul v-if="memberHistory.length" class="max-h-64 space-y-1 overflow-y-auto p-3">
							<li v-for="session in memberHistory" :key="session.id">
								<button
									type="button"
									class="hover:bg-muted/50 flex w-full items-baseline gap-3 px-2 py-1.5 text-left"
									:class="session.id === sessionId ? 'bg-muted' : ''"
									@click="openSession(session.id)"
								>
									<span class="text-muted-foreground shrink-0 text-xs tabular-nums">
										{{ formatAgo(now - new Date(session.updatedAt).getTime()) }}
									</span>
									<span class="min-w-0 flex-1 truncate">{{ preview(session) }}</span>
									<span v-if="session.status === 'closed'" class="text-muted-foreground shrink-0 text-xs">Closed</span>
									<ZhSystemLabel v-if="unreadIn(session)" as="span" tone="system" class="shrink-0">
										{{ unreadIn(session) }} Unread
									</ZhSystemLabel>
								</button>
							</li>
						</ul>
						<p v-else class="text-muted-foreground px-4 py-3 text-xs">No chats yet.</p>
					</section>
					<section v-if="grantsOpen" data-testid="chat-tool-grants" class="border-b text-sm">
						<header class="bg-muted/20 flex items-center justify-between gap-3 border-b px-4 py-3">
							<ZhSectionHeading>
								<template #default>Allowed tools</template>
								<template v-if="grants.length" #count>{{ grants.length }}</template>
							</ZhSectionHeading>
							<p class="text-muted-foreground min-w-0 truncate text-xs">
								Tools {{ memberLabel(detail.member) }} runs in your chats without asking
							</p>
						</header>
						<ul v-if="grants.length" class="max-h-64 space-y-1 overflow-y-auto p-3">
							<li v-for="grant in grants" :key="grant.id" class="flex items-center gap-3 px-2 py-1">
								<span class="min-w-0 flex-1 truncate font-mono text-xs">{{ shownTool(grant.tool) }}</span>
								<Button
									type="button"
									size="xs"
									variant="outline"
									:disabled="revokingGrantId === grant.id"
									@click="revokeGrant(grant)"
								>
									Revoke
								</Button>
							</li>
						</ul>
						<p v-else class="text-muted-foreground px-4 py-3 text-xs">
							None: {{ memberLabel(detail.member) }} asks before any tool that can change something.
						</p>
					</section>
					<p v-if="error" class="text-destructive px-4 pt-2 text-sm">{{ error }}</p>
					<!-- A withdrawal the API refused (issue 284), kept through the reads that follow it. -->
					<p
						v-if="withdrawNotice && withdrawNotice.chatId === detail.id"
						data-testid="chat-withdraw-refused"
						class="text-destructive px-4 pt-2 text-sm"
						role="alert"
					>
						{{ withdrawNotice.text }}
					</p>
				</template>
				<template #log-end>
					<ZhEmptyState v-if="viewState === 'empty'" data-testid="chat-empty" label="NO MESSAGES">
						Nothing said yet. Send {{ memberLabel(detail.member) }} the first message below to start.
					</ZhEmptyState>
				</template>
				<!-- The tool calls waiting on you come between: ChatConversation draws their cards (ChatToolConfirm). -->
				<template #log-after>
					<div
						v-if="indicatorActive"
						data-testid="chat-run-indicator"
						:data-run-status="trackedStatus ?? undefined"
						class="text-muted-foreground max-w-xl space-y-1 border px-3 py-2 text-xs"
					>
						<p v-if="indicatorQueued" class="flex items-center gap-2">
							<ZhStatusDot state="inactive" />
							Queued · waiting for a free slot
						</p>
						<p v-else class="flex items-center gap-2">
							<ZhStatusDot state="active" />
							Work in progress · {{ formatDuration(trackedElapsedMs ?? 0) }}
						</p>
						<p v-if="trackedStep" data-testid="chat-run-step">{{ trackedStep.type }}</p>
					</div>
				</template>
				<template #message-extra="{ entry }">
					<!-- Each file the message carried, as a chip, oldest first. -->
					<div
						v-if="entry.id && filesByKey.get(entry.id)"
						class="mt-2 grid gap-2"
						role="group"
						aria-label="Files on this message"
						data-testid="chat-message-files"
					>
						<ZhFileChip
							v-for="file in filesByKey.get(entry.id)"
							:key="file.id"
							:filename="file.filename"
							:type-label="chatFileKindLabel(file.type)"
							:size="formatChatFileSize(file.sizeBytes)"
							:thumbnail-src="fileThumbnails.get(file.id)"
							:notice="unavailableFiles.has(file.id) ? CHAT_FILE_UNAVAILABLE : undefined"
							:on-preview="isChatFilePreviewable(file.type) ? () => previewFile(file) : undefined"
							:on-open="() => openFile(file)"
							:on-download="() => downloadFile(file)"
						/>
					</div>
					<!-- The skip link, under the COO's first message once the AI account step is complete (os 1742). -->
					<Button
						v-if="offerSkip && entry.kind === 'assistant' && entry.id && entry.id === firstReplyKey"
						type="button"
						size="sm"
						variant="link"
						class="mt-2 h-auto p-0 text-left whitespace-normal"
						data-testid="chat-skip-setup"
						:disabled="setupMoving"
						@click="skipSetup"
					>
						{{ SKIP_SETUP }}
					</Button>
					<ZhSystemLabel
						v-if="entry.kind === 'user' && entry.id && waitingKeys.has(entry.id)"
						data-testid="chat-message-waiting"
						tone="muted"
					>
						Waiting · not read yet
					</ZhSystemLabel>
					<!-- Take a waiting message back before the member reads it (issue 284): only where the API says you may. -->
					<Button
						v-if="entry.kind === 'user' && entry.id && withdrawableKeys.has(entry.id)"
						type="button"
						size="sm"
						variant="link"
						class="mt-1 h-auto p-0"
						data-testid="chat-message-withdraw"
						:disabled="withdrawingId !== null"
						@click="withdraw(entry.id)"
					>
						Withdraw
					</Button>
					<ZhSystemLabel
						v-if="entry.id && endedByKey.get(entry.id)"
						data-testid="chat-reply-ended"
						tone="muted"
					>
						{{ endedByKey.get(entry.id) }}
					</ZhSystemLabel>
					<ChatMemoryLog
						v-if="entry.id && memoryByKey.get(entry.id)"
						:summary="memoryByKey.get(entry.id)?.summary ?? ''"
						:notes="memoryByKey.get(entry.id)?.notes ?? []"
					/>
				</template>
				<!-- The reply's tool log comes between: ChatConversation draws it from `toolLogOf`. -->
				<template #message-end="{ entry }">
					<!--
						A gate's card stays where it was raised (issue 1981): waiting, with its controls, until it is decided
						anywhere, then settled on its outcome for good, with none. The same card either way, so one decided
						while the thread is open turns in place.
					-->
					<!-- Issue 849: a run's proposed work the lead put to you, decided item by item or all at once, here or anywhere. -->
					<ProposedWorkCard
						v-if="proposedByKey.get(entry.id ?? '')"
						variant="inline"
						:batch="proposedByKey.get(entry.id ?? '')?.batch ?? null"
						:now="now"
						@decided="batch => proposedDecided(proposedByKey.get(entry.id ?? '')?.runId ?? '', batch)"
					/>
					<ZhDecisionCard
						v-for="card in gatesByKey.get(entry.id ?? '') ?? []"
						:key="card.id"
						data-testid="chat-gate-card"
						variant="inline"
						:kind="card.waiting ? gateKind(card.waiting) : undefined"
						:decision="card.decision"
						:settled="card.settled?.outcome"
						:decided-label="card.settled?.label"
						:settled-lines="card.settled?.lines"
						:settled-when="card.settled?.when"
					>
						<template v-if="card.waiting" #actions>
							<div class="w-full space-y-3 text-sm">
								<GateDecisionActions
									:gate="card.waiting"
									:busy="decidingGateId === card.id"
									@decide="handleDecide"
								/>
							</div>
						</template>
					</ZhDecisionCard>
				</template>
				<template #composer-before>
					<!--
						The member's own step list (issue 286): directly above the composer, outside the scrolling
						messages, so it stays in view. A thread with no list draws nothing here. Below md it starts as
						its one-line summary and opens on tap. Its heading says whose list it is, and the hint says how
						to change it: the person can't edit it, they tell the member.
					-->
					<ZhStepList
						v-if="steps.length > 0"
						data-testid="chat-steps"
						class="shrink-0 border-t px-4 py-2 md:px-6"
						:steps="steps"
						:collapsed="!wideScreen"
						:label="`Steps ${memberLabel(detail.member)} has lined up`"
						:hint="`To change or skip a step, tell ${memberLabel(detail.member)} in a message.`"
					/>
				</template>
				<!-- The composer itself (message box and Send) is ChatConversation's (ChatComposer); these sit in it. -->
				<template #composer-notices>
						<p
							v-if="keyInChat"
							role="alert"
							data-testid="chat-key-refused"
							class="text-destructive mb-2 text-sm"
						>
							{{ PROVIDER_KEY_IN_CHAT_TEXT }}
							<RouterLink class="underline" :to="ACCOUNT_STEP_PATH">Open the AI account card</RouterLink>
						</p>
						<p
							v-if="attachRefusal"
							role="alert"
							data-testid="chat-attach-refused"
							class="text-destructive mb-2 text-sm"
						>
							{{ attachRefusal }}
						</p>
						<div
							v-if="pendingFiles.length > 0 || uploads.length > 0"
							class="mb-2 flex flex-wrap gap-2"
							role="group"
							aria-label="Files to send"
						>
							<ZhFileChip
								v-for="file in pendingFiles"
								:key="file.id"
								data-testid="chat-pending-file"
								:filename="file.filename"
								:type-label="chatFileKindLabel(file.type)"
								:size="formatChatFileSize(file.sizeBytes)"
								:notice="heldNotice(file)"
								:on-remove="() => removeHeld(file)"
							/>
							<!-- Files on their way (issue 2085): uploading, or failed with Retry; each can be taken off. -->
							<ZhFileChip
								v-for="item in uploads"
								:key="`upload-${item.key}`"
								data-testid="chat-pending-file"
								:filename="item.file.name"
								:type-label="chatFileKindLabel(chatFileKind(item.file.name))"
								:size="formatChatFileSize(item.file.size)"
								:state="item.state"
								:notice="imageNotice(chatFileKind(item.file.name))"
								:on-retry="item.state === 'failed' ? () => retryUpload(item.key) : undefined"
								:on-remove="() => removeUpload(item)"
							/>
						</div>
						<!--
							The setup conversation's open question, as chips (the kind of work, then the role, then
							how technical), each saving through the setup API. Keyed by the question, so a typed
							answer begun under one never carries into the next.
						-->
						<ZhQuickReplies
							v-if="setupReplyChips"
							:key="`${setupOpen ?? 'none'}:${roleGuess ? 'confirm' : 'ask'}`"
							v-model:draft="setupDraft"
							data-testid="chat-setup-replies"
							:data-setup-question="setupOpen ?? undefined"
							class="mb-2"
							:replies="setupReplyChips.replies"
							:allow-other="setupReplyChips.otherLabel !== null"
							:other-label="setupReplyChips.otherLabel ?? undefined"
							input-label="Your answer"
							:disabled="setupSaving || detail.status === 'closed' || decidingHere"
							@select="chooseSetupChip"
							@text="answerSetupTyped"
						/>
						<!-- At team_proposal: the teams setup proposes, one chip each; a choice saves the proposal. -->
						<ZhQuickReplies
							v-if="setupProposalReplies.length > 0"
							data-testid="chat-setup-proposal"
							class="mb-2"
							:replies="setupProposalReplies"
							:disabled="setupSaving || detail.status === 'closed' || decidingHere"
							@select="chooseSetupTeam"
						/>
						<!-- At names: the names form, keyed by the proposed template, so a new proposal starts it afresh. -->
						<SetupNamesForm
							v-if="setupNamesTemplate"
							:key="setupNamesTemplate.id"
							data-testid="chat-setup-names"
							class="mb-2"
							:template="setupNamesTemplate"
							:busy="setupSaving"
							:failure="setupNamesFailure"
							@save="saveSetupNames"
							@skip="saveSetupNames(null)"
						/>
						<!-- From assembly on: the log of real setup events, fed only by the server's event read. -->
						<AssemblyLog
							v-if="setupAssemblyId"
							:key="setupAssemblyId"
							:assembly-id="setupAssemblyId"
							class="mb-2"
							@finished="reloadSetup"
						/>
						<!-- At team setup: Connect for the one item the cloned team needs now. -->
						<ZhQuickReplies
							v-if="setupConnectReplies.length > 0"
							data-testid="chat-setup-connect"
							class="mb-2"
							:replies="setupConnectReplies"
							:disabled="detail.status === 'closed'"
							@select="connectSetupTool"
						/>
						<!-- After team setup: the optional tools step, opened on its own screen or skipped. -->
						<div
							v-if="offerMoreTools"
							class="mb-2 flex flex-wrap items-center gap-2"
							data-testid="chat-setup-more-tools"
						>
							<Button
								type="button"
								size="sm"
								:disabled="detail.status === 'closed'"
								@click="openMoreTools"
							>
								{{ MORE_TOOLS_TITLE }}
							</Button>
							<Button
								type="button"
								size="sm"
								variant="ghost"
								data-testid="chat-setup-more-tools-skip"
								:disabled="setupMoving || detail.status === 'closed'"
								@click="skipMoreTools"
							>
								{{ MORE_TOOLS_SKIP }}
							</Button>
						</div>
						<!-- Stop (issue 281): while the member replies, at once and with no confirmation. -->
						<div v-if="stopShown" class="mb-2 flex flex-wrap items-center gap-3">
							<Button
								type="button"
								size="sm"
								variant="outline"
								data-testid="chat-stop"
								:disabled="stopPending"
								@click="stopReply"
							>
								{{ stopPending ? 'Stopping…' : 'Stop' }}
							</Button>
							<p
								role="status"
								aria-live="polite"
								:class="stopPending ? 'text-muted-foreground text-xs' : 'sr-only'"
								data-testid="chat-stop-status"
							>
								{{ stopPending ? STOPPING_NOTE : '' }}
							</p>
							<p v-if="stopRefused" role="alert" class="text-destructive text-xs" data-testid="chat-stop-refused">
								{{ STOP_REFUSED }}
							</p>
							<!-- Only with a run still working: the note points at its run page, and with none it points nowhere. -->
							<p v-if="activeThreadRuns.length" class="text-muted-foreground text-xs" data-testid="chat-stop-note">
								{{ STOP_NOTE }}
								<template v-for="run in activeThreadRuns" :key="run.id">
									{{ ' ' }}<RouterLink class="underline" :to="`/runs/${run.id}`">{{ run.label }}</RouterLink>
								</template>
							</p>
						</div>
						<p v-if="stoppedHintShown" class="text-muted-foreground mb-2 text-xs" data-testid="chat-stopped-hint">
							{{ STOPPED_HINT }}
						</p>
				</template>
				<template #composer-tools>
								<Button
									type="button"
									size="sm"
									variant="outline"
									data-testid="chat-attach-button"
									:disabled="detail.status === 'closed'"
									@click="chooseFiles"
								>
									Attach
								</Button>
								<input
									ref="attachInput"
									data-testid="chat-attach-input"
									type="file"
									multiple
									class="hidden"
									@change="onChosen"
								/>
								<p class="text-muted-foreground text-xs" data-testid="chat-file-limits">
									File limits: {{ fileLimits }}
								</p>
				</template>
			</ChatConversation>
			<p v-else-if="viewState === 'error'" data-testid="chat-error" class="text-destructive p-8 text-sm" role="alert">
				{{ error }}
			</p>
			<div
				v-else-if="viewState === 'loading'"
				data-testid="chat-loading"
				class="flex min-h-0 flex-1 flex-col"
				role="status"
				aria-busy="true"
			>
				<span class="sr-only">Loading chat…</span>
				<div class="flex items-center gap-3 border-b px-4 py-3">
					<Skeleton class="size-9 shrink-0 rounded-full" />
					<Skeleton class="h-4 w-40 rounded-none" />
				</div>
				<div class="flex flex-1 flex-col gap-3 p-4">
					<Skeleton v-for="bubble in LOADING_BUBBLES" :key="bubble.key" class="max-w-xl rounded-none" :class="bubble.class" />
				</div>
				<div class="border-t p-3">
					<Skeleton class="h-16 w-full rounded-none" />
				</div>
			</div>
			<div v-else class="text-muted-foreground hidden flex-1 items-center justify-center p-8 text-sm md:flex">
				Pick someone to talk to.
			</div>
		</div>

		<!--
			Below md the member list is off-canvas behind the thread's Members trigger: the shared Sheet, the one the
			sidebar primitive opens on a phone, from the same side and at the same width. From md up the list is the
			inline aside above, and the trigger is hidden.
		-->
		<Sheet v-model:open="listOpen">
			<SheetContent side="left" class="w-72 gap-0 p-0" data-testid="chat-list-sheet">
				<SheetHeader class="border-b p-4">
					<SheetTitle>Chat</SheetTitle>
					<SheetDescription>{{ CHAT_INTRO }}</SheetDescription>
				</SheetHeader>
				<ZhChatList
					class="min-h-0 flex-1 overflow-y-auto p-2"
					:items="people"
					label="Team members"
					selectable
					@select="selectFromSheet"
				>
					<template #avatar="{ item }">
						<MemberAvatar :member="{ name: item.member.name, avatarUrl: item.member.avatarUrl ?? null }" class="size-full" />
					</template>
				</ZhChatList>
			</SheetContent>
		</Sheet>

		<!--
			An image or a PDF previewed inside the chat, from its bytes: the kit's ZhFilePreview draws the full image,
			or the PDF in the browser's own viewer. Closing it lets go of the file's URL.
		-->
		<Dialog :open="filePreview !== null" @update:open="onFilePreviewOpen">
			<DialogContent v-if="filePreview" class="sm:max-w-3xl" data-testid="chat-file-preview">
				<DialogHeader>
					<DialogTitle class="truncate pr-8" :title="filePreview.file.filename">{{ filePreview.file.filename }}</DialogTitle>
					<DialogDescription>
						{{ chatFileKindLabel(filePreview.file.type) }} · {{ formatChatFileSize(filePreview.file.sizeBytes) }}
					</DialogDescription>
				</DialogHeader>
				<ZhFilePreview :src="filePreview.url" :filename="filePreview.file.filename" :image="filePreview.image" />
			</DialogContent>
		</Dialog>
	</section>
</template>

<style scoped>
/* The shared thread at working density: the head and composer stay, the thread under them scrolls. */
.chat-thread { display: flex; flex: 1; flex-direction: column; min-height: 0; }
/*
 * Mobile first: the head wraps its actions under the member rather than running off a phone's edge, and the
 * log's one column may shrink below its widest item, so a wide message scrolls or wraps inside the thread
 * instead of widening it.
 */
.chat-thread :deep(.zh-chat__head) { flex-shrink: 0; flex-wrap: wrap; padding: 0.75rem 1rem; }
.chat-thread :deep(.zh-chat__log) { display: grid; flex: 1; grid-template-columns: minmax(0, 1fr); align-content: start; gap: 0.75rem; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 1rem; }
.chat-thread :deep(.zh-chat__messages) { padding-block: 0; }
/* The gutters step up at the tablet boundary (Tailwind's md, 48rem), as the composer's do. */
@media (min-width: 48rem) {
    .chat-thread :deep(.zh-chat__head) { padding: 1rem 1.5rem; }
    .chat-thread :deep(.zh-chat__log) { padding: 1.25rem 1.5rem; }
}
/*
 * The bubbles themselves are ZhChatThread's alone (#972): their v2 type, spacing and colour are not
 * resized or re-spaced here, and what ChatToolLog puts in one styles itself. Only their measure is the page's:
 * a message may take nearly the thread's whole width, so a long reply on a wide screen needs less scrolling.
 *
 * Nearly: the member's side (replies, runs, decisions, thinking) keeps a margin on the right and yours a margin on
 * the left, so whose message is whose still reads at a glance. The margin grows with the screen, at Tailwind's
 * breakpoints.
 */
.chat-thread { --zh-chat-bubble-max: 100%; --chat-side-margin: 2rem; }
.chat-thread :deep(.zh-chat__msg) { padding-inline-end: var(--chat-side-margin); }
.chat-thread :deep(.zh-chat__msg[data-kind="user"]) { padding-inline: var(--chat-side-margin) 0; }
@media (min-width: 48rem) { .chat-thread { --chat-side-margin: 3rem; } }
@media (min-width: 64rem) { .chat-thread { --chat-side-margin: 5rem; } }
@media (min-width: 80rem) { .chat-thread { --chat-side-margin: 7rem; } }
@media (min-width: 96rem) { .chat-thread { --chat-side-margin: 10rem; } }
</style>
